'use client';

import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh';
import 'dayjs/locale/ru';
import { getLanguage } from '@/utils/localStorageControl';

// 성형 수술 종류 정의
type SurgeryType = 
  | '코성형' 
  | '눈성형'
  | '안면윤곽'
  | '가슴성형'
  | '지방이식'
  | '기타'
  | string; // 사용자 정의 수술 타입 허용

interface SurgerySchedule {
  id: string;
  type: SurgeryType;
  consultationDate: string | null;
  surgeryDate: string | null;
  stitchRemovalDate: string | null;
}

interface SimpleCalendarProps {
  isFullWidth?: boolean;
}

export default function SimpleCalendar({ isFullWidth = false }: SimpleCalendarProps) {
  const [currentLang, setCurrentLang] = useState<string>('ko');
  const [currentMonth, setCurrentMonth] = useState<dayjs.Dayjs>(dayjs());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [surgerySchedules, setSurgerySchedules] = useState<SurgerySchedule[]>([]);
  const [currentSchedule, setCurrentSchedule] = useState<SurgerySchedule>({
    id: Date.now().toString(),
    type: '코성형',
    consultationDate: null,
    surgeryDate: null,
    stitchRemovalDate: null
  });
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'calendar' | 'settings'>('calendar');
  
  // 날짜 정보와 케어 메시지
  const [careInfo, setCareInfo] = useState<string | null>(null);
  
  // 기기 테마 감지
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // 색상 설정 - 주요 색상
  const colors = {
    primary: '#3B82F6', // 밝은 파란색
    secondary: '#10B981', // 그린
    highlight: '#EF4444', // 빨간색
  };
  
  // 문자열 해시 생성 함수 (사용자 정의 수술 유형의 색상 생성에 사용)
  const hashStringToNumber = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0; // 32비트 정수로 변환
    }
    return Math.abs(hash);
  };
  
  // 수술 타입별 색상 반환 함수 (최대 3가지 색상으로 제한)
  const getSurgeryTypeColor = (type: SurgeryType): string => {
    switch (type) {
      case '코성형': return colors.primary; // 파란색
      case '눈성형': return colors.primary; // 파란색
      case '안면윤곽': return colors.secondary; // 그린
      case '가슴성형': return colors.secondary; // 그린
      case '지방이식': return colors.highlight; // 빨간색
      case '기타': return colors.highlight; // 빨간색
      default:
        // 사용자 정의 수술 타입도 3가지 색상 중 하나만 사용
        const hash = hashStringToNumber(type);
        const colorIndex = hash % 3; // 0, 1, 2 중 하나
        return colorIndex === 0 ? colors.primary : 
               colorIndex === 1 ? colors.secondary : 
               colors.highlight;
    }
  };
  useEffect(() => {
    setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  // 로컬 스토리지에서 수술 정보 로드
  useEffect(() => {
    try {
      // 언어 설정
      const lang = getLanguage() || 'ko';
      setCurrentLang(lang);
      
      // 메인 페이지 캘린더는 예시 데이터를 사용 (로컬 스토리지 연동 X)
      if (isFullWidth) {
        // 캘린더 페이지일 경우만 로컬 스토리지에서 저장된 수술 스케줄 불러오기
        const savedSchedules = localStorage.getItem('surgerySchedules');
        if (savedSchedules) {
          const schedules = JSON.parse(savedSchedules);
          setSurgerySchedules(schedules);
          
          // 스케줄이 있으면 첫 번째 스케줄을 현재 스케줄로 설정
          if (schedules.length > 0) {
            setCurrentSchedule(schedules[0]);
            setSelectedScheduleId(schedules[0].id);
          }
        }
      } else {
        // 메인페이지일 경우 예시 데이터 설정
        const exampleSchedules: SurgerySchedule[] = [
          {
            id: '1',
            type: '코성형',
            consultationDate: dayjs().subtract(14, 'day').format('YYYY-MM-DD'),
            surgeryDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
            stitchRemovalDate: dayjs().add(14, 'day').format('YYYY-MM-DD')
          }
        ];
        setSurgerySchedules(exampleSchedules);
        setCurrentSchedule(exampleSchedules[0]);
        setSelectedScheduleId(exampleSchedules[0].id);
      }
      
      // Map language code to dayjs locale
      const localeMap: {[key: string]: string} = {
        'ko': 'ko',
        'en': 'en',
        'ja': 'ja',
        'ch': 'zh',
        'ru': 'ru'
      };
      
      dayjs.locale(localeMap[lang] || 'ko');
    } catch (error) {
      console.error('캘린더 초기화 오류:', error);
    }
  }, [isFullWidth]);
  
  // 수술 스케줄 저장
  const saveSurgerySchedules = (schedules: SurgerySchedule[]) => {
    try {
      localStorage.setItem('surgerySchedules', JSON.stringify(schedules));
      setSurgerySchedules(schedules);
    } catch (error) {
      console.error('수술 스케줄 저장 오류:', error);
    }
  };
  
  // 현재 스케줄 저장
  const saveCurrentSchedule = () => {
    try {
      // 메인페이지에서는 저장하지 않음 (예시 데이터만 사용)
      if (!isFullWidth) {
        setCurrentView('calendar');
        return;
      }
      
      // 중복 스케줄 체크 (같은 수술 타입과 수술 날짜가 있는지 확인)
      const hasDuplicate = surgerySchedules.some(
        schedule => 
          schedule.id !== currentSchedule.id && 
          schedule.type === currentSchedule.type && 
          schedule.surgeryDate === currentSchedule.surgeryDate
      );
      
      if (hasDuplicate) {
        const confirmSave = confirm('동일한 수술 타입과 날짜의 일정이 이미 존재합니다. 그래도 저장하시겠습니까?');
        if (!confirmSave) return;
      }
      
      // 현재 스케줄이 이미 목록에 있는지 확인
      const existingIndex = surgerySchedules.findIndex(schedule => schedule.id === currentSchedule.id);
      
      let updatedSchedules;
      if (existingIndex >= 0) {
        // 기존 스케줄 업데이트
        updatedSchedules = [...surgerySchedules];
        updatedSchedules[existingIndex] = currentSchedule;
      } else {
        // 새 스케줄 추가
        updatedSchedules = [...surgerySchedules, currentSchedule];
      }
      
      saveSurgerySchedules(updatedSchedules);
      setSelectedScheduleId(currentSchedule.id);
      setCurrentView('calendar');
    } catch (error) {
      console.error('수술 스케줄 저장 오류:', error);
    }
  };
  
  // 새 수술 스케줄 생성
  const createNewSchedule = () => {
    // 새로운 ID로 빈 스케줄 생성
    const newSchedule = {
      id: Date.now().toString(),
      type: '코성형',
      consultationDate: null,
      surgeryDate: null,
      stitchRemovalDate: null
    };
    
    setCurrentSchedule(newSchedule);
    setSelectedScheduleId(newSchedule.id);
  };
  
  // 스케줄 삭제
  const deleteSchedule = (id: string) => {
    const updatedSchedules = surgerySchedules.filter(schedule => schedule.id !== id);
    saveSurgerySchedules(updatedSchedules);
    
    // 삭제 후 남은 스케줄이 있으면 첫 번째 스케줄을 선택
    if (updatedSchedules.length > 0) {
      setCurrentSchedule(updatedSchedules[0]);
      setSelectedScheduleId(updatedSchedules[0].id);
    } else {
      // 남은 스케줄이 없으면 새 스케줄 생성
      const newSchedule = {
        id: Date.now().toString(),
        type: '코성형',
        consultationDate: null,
        surgeryDate: null,
        stitchRemovalDate: null
      };
      setCurrentSchedule(newSchedule);
      setSelectedScheduleId(null);
    }
  };
  
  // 날짜 선택 시 케어 정보 업데이트
  const handleDayClick = (dateStr: string) => {
    setSelectedDay(dateStr);
    
    // 해당 날짜의 직접적인 일정 (상담일, 수술일, 실밥제거일)이 있는 스케줄 찾기
    const directSchedulesForDate = surgerySchedules.filter(schedule => 
      schedule.consultationDate === dateStr || 
      schedule.surgeryDate === dateStr || 
      schedule.stitchRemovalDate === dateStr
    );
    
    // 해당 날짜가 어떤 수술의 케어 기간에 속하는지 체크 (수술 전후 기간)
    const allSchedulesForDate = surgerySchedules.filter(schedule => {
      if (schedule.surgeryDate) {
        // 수술 날짜가 설정된 경우만 체크
        const date = dayjs(dateStr);
        const surgeryDate = dayjs(schedule.surgeryDate);
        
        // 상담일, 수술일, 실밥제거일이거나, 수술일 기준으로 전후 일정이 있는지 확인
        return (
          schedule.consultationDate === dateStr || 
          schedule.surgeryDate === dateStr || 
          schedule.stitchRemovalDate === dateStr ||
          // 수술 전후 180일 이내인지 체크 (약 6개월 전후 기간)
          Math.abs(date.diff(surgeryDate, 'day')) <= 180
        );
      }
      return false;
    });
    
    // 직접적인 일정이 없고, 수술 관련 정보도 없는 경우
    if (allSchedulesForDate.length === 0) {
      // 선택된 스케줄이 있으면 그 스케줄 사용
      const selectedSchedule = surgerySchedules.find(s => s.id === selectedScheduleId);
      
      if (!selectedSchedule) {
        setCareInfo('수술 일정을 먼저 설정해주세요. 수술 정보 설정 버튼을 눌러 일정을 추가하시면 맞춤형 케어 정보를 확인하실 수 있습니다.');
        return;
      }
      
      if (!selectedSchedule.surgeryDate) {
        setCareInfo('수술 날짜를 먼저 설정해주세요. 수술 정보 설정 버튼을 눌러 날짜를 입력하시면 맞춤형 케어 정보를 확인하실 수 있습니다.');
        return;
      }
      
      setCareInfo('선택하신 날짜에 대한 케어 정보가 없습니다.');
      return;
    }
    
    // 직접적인 일정이 있으면 (상담일, 수술일, 실밥제거일) 그 일정들 우선 처리
    if (directSchedulesForDate.length > 0) {
      // 선택된 스케줄이 있고 그 스케줄이 이 날짜에 일정이 있으면 우선 사용
      let selectedDirectSchedule = directSchedulesForDate.find(s => s.id === selectedScheduleId);
      
      // 선택된 스케줄이 이 날짜에 일정이 없으면 첫 번째 스케줄 사용
      if (!selectedDirectSchedule) {
        selectedDirectSchedule = directSchedulesForDate[0];
        // 선택된 스케줄 업데이트
        setSelectedScheduleId(selectedDirectSchedule.id);
      }
    }
    
    // 모든 관련 스케줄에 대한 케어 정보 생성 (상담일/수술일/실밥제거일 + 수술 전후 케어)
    
    // 먼저 모든 스케줄 중에서 선택된 스케줄 또는 직접 관련 있는 스케줄이 가장 앞에 오도록 정렬
    allSchedulesForDate.sort((a, b) => {
      // 선택된 스케줄이 가장 앞으로
      if (a.id === selectedScheduleId) return -1;
      if (b.id === selectedScheduleId) return 1;
      
      // 그 다음 직접적인 일정(상담일, 수술일, 실밥제거일)이 있는 스케줄
      const aHasDirectEvent = a.consultationDate === dateStr || a.surgeryDate === dateStr || a.stitchRemovalDate === dateStr;
      const bHasDirectEvent = b.consultationDate === dateStr || b.surgeryDate === dateStr || b.stitchRemovalDate === dateStr;
      
      if (aHasDirectEvent && !bHasDirectEvent) return -1;
      if (!aHasDirectEvent && bHasDirectEvent) return 1;
      
      return 0;
    });
    
    // 각 스케줄별 케어 정보 생성
    let combinedMessages = '';
    const date = dayjs(dateStr);
    
    for (const schedule of allSchedulesForDate) {
      let message = '';
      let title = '';
      
      // 현재 해당 날짜의 상태를 확인
      const isConsultationDay = dateStr === schedule.consultationDate;
      const isSurgeryDay = dateStr === schedule.surgeryDate;
      const isStitchRemovalDay = dateStr === schedule.stitchRemovalDate;
      
      if (isConsultationDay) {
        title = `🗓️ ${schedule.type} 상담일`;
        message = getCareMessageForConsultation(schedule.type);
      } else if (isSurgeryDay) {
        title = `🏥 ${schedule.type} 수술일`;
        message = getCareMessageForSurgeryDay(schedule.type);
      } else if (isStitchRemovalDay) {
        title = `🩹 ${schedule.type} 실밥제거일`;
        message = getCareMessageForStitchRemoval(schedule.type);
      } else if (schedule.surgeryDate) {
        // 수술일 기준 D-day 계산
        const surgeryDate = dayjs(schedule.surgeryDate);
        const dayDiff = date.diff(surgeryDate, 'day');
        
        if (dayDiff < 0) {
          // 수술 전
          title = `🔜 ${schedule.type} 수술 ${Math.abs(dayDiff)}일 전`;
          message = getCareMessageBeforeSurgery(schedule.type, dayDiff);
        } else if (dayDiff > 0) {
          // 수술 후
          title = `🔄 ${schedule.type} 수술 후 ${dayDiff}일차`;
          message = getCareMessageAfterSurgery(schedule.type, dayDiff);
        }
      }
      
      // 정보가 있는 경우에만 추가
      if (message) {
        // 수술 타입별 색상 지정
        const typeColor = getSurgeryTypeColor(schedule.type);
        
        const coloredTitleBar = `\n\n${'='.repeat(5)} <span style="color:${typeColor}; font-weight:bold;">${title}</span> ${'='.repeat(5)}\n\n`;
        
        // 정보가 여러 개일 경우 구분선으로 구분
        if (combinedMessages) {
          combinedMessages += `\n\n${'- '.repeat(15)}\n${coloredTitleBar}${message}`;
        } else {
          combinedMessages = `${coloredTitleBar}${message}`;
        }
      }
    }
    
    if (combinedMessages) {
      // 하나 이상의 일정이 있는 경우
      if (allSchedulesForDate.length > 1) {
        const headerText = `📅 ${dateStr} - 복합 케어 정보`;
        setCareInfo(`${headerText}${combinedMessages}`);
      } else {
        // 단일 일정인 경우
        const schedule = allSchedulesForDate[0];
        const headerText = `📅 ${dateStr} - ${schedule.type} 케어 정보`;
        setCareInfo(`${headerText}${combinedMessages}`);
      }
    } else {
      setCareInfo('선택하신 날짜에 대한 케어 정보가 없습니다.');
    }
  };
  
  // 수술 전 케어 메시지
  const getCareMessageBeforeSurgery = (type: SurgeryType, dayDiff: number): string => {
    const days = Math.abs(dayDiff);
    
    // 수술 종류별 사전 준비물
    const getSurgeryPreparationItems = (type: SurgeryType): string => {
      switch (type) {
        case '코성형':
          return "준비물: 입술 보습제, 얼음팩, 거즈, 수분 스프레이, 목 베개, 누웠을 때 사용할 수 있는 스마트폰/태블릿 거치대, 부드러운 식사";
        case '눈성형':
          return "준비물: 얼음팩, 인공눈물, 선글라스, 눈 가리개, 머리를 고정할 수 있는 베개, 자극이 적은 세안제";
        case '안면윤곽':
          return "준비물: 얼음팩, 목 베개, 영양제, 얼굴을 감쌀 수 있는 압박 붕대, 죽이나 부드러운 식사, 빨대";
        case '가슴성형':
          return "준비물: 앞여밈 상의, 수술 후 전용 브라, 가슴 압박 붕대, 허리 베개, 영양제";
        case '지방이식':
          return "준비물: 압박 의류, 얼음팩, 영양제, 쿠션, 흉터 케어 크림";
        default:
          return "준비물: 편안한 복장, 얼음팩, 회복에 도움이 되는 영양제, 수술 부위에 맞는 관리 용품";
      }
    };
    
    if (days > 28) {
      return `수술 4주 이상 전: 
      
• 금주/금연을 시작하세요
• 정기적인 운동으로 체력을 기르세요
• 비타민 C, 단백질 등 영양소를 충분히 섭취하세요
• 충분한 수면을 취하세요
• 필요한 검사(혈액검사, 흉부 X선 등)를 미리 받아두세요

${getSurgeryPreparationItems(type)}

🚫 금지사항: 다이어트, 과로, 음주, 흡연`;
    } else if (days > 14) {
      return `수술 2-4주 전: 

• 혈액 응고에 영향을 주는 약물(아스피린 등)의 복용을 의사와 상담 후 중단하세요
• 비타민E 보충제, 이부프로펜, 생강, 은행 등 출혈을 늘릴 수 있는 보충제 복용을 중단하세요
• 흡연자라면 반드시 금연하세요 (회복에 심각한 영향을 줍니다)
• 술은 수술 2주 전부터 완전히 끊으세요
• 수술 후 도움을 줄 가족/친구를 미리 섭외해두세요

🧳 여행: 장거리 여행은 수술 2주 전까지 마무리하세요

${getSurgeryPreparationItems(type)}

🚫 금지사항: 과도한 일정, 무리한 운동, 음주, 흡연, 혈액 희석제`;
    } else if (days > 7) {
      return `수술 1-2주 전:

• 수술 후 사용할 편안한 옷을 준비하세요 (앞여밈 셔츠, 느슨한 바지)
• 미리 식사를 준비해두어 수술 후 쉽게 먹을 수 있게 하세요
• 수술 후 휴식을 취할 공간을 편안하게 정리해두세요
• 관리자와 병원의 연락처를 저장해두세요
• 수술 전/후 복용할 약을 확인하고 준비하세요

🏋️ 운동: 가벼운 운동은 괜찮지만, 격렬한 운동은 수술 1주일 전부터 중단하세요
🧳 여행: 이 시기에는 가급적 여행을 피하세요

${getSurgeryPreparationItems(type)}

🚫 금지사항: 새로운 피부 관리 제품 사용, 피부 필링, 염색, 파마`;
    } else if (days > 3) {
      return `수술 3-7일 전:

• 충분한 수분을 섭취하세요
• 채소와 과일을 충분히 섭취하고 건강한 식단을 유지하세요
• 수술 후 필요한 약품(진통제 등)을 미리 구비해두세요
• 휴가 계획을 확정하고 주변에 알려두세요
• 수술 후 간병이 필요한 경우 도움을 요청해두세요

👨‍💼 업무: 수술 후 쉴 수 있도록 업무 일정을 조정하세요
🚗 운전: 수술 전날까지는 운전해도 됩니다
${getSurgeryPreparationItems(type)}

🚫 금지사항: 술, 담배, 카페인, 매운 음식, 아스피린, 이부프로펜`;
    } else if (days > 1) {
      return `수술 1-2일 전:

• 수술 전날 저녁은 가볍게 드세요
• 몸을 깨끗이 씻고 화장은 하지 마세요
• 손톱의 매니큐어를 지우세요
• 귀중품과 액세서리는 집에 두고 가세요
• 수술 당일 입을 편안한 옷을 준비하세요

🧳 여행 준비: 수술 후 숙소로 바로 이동할 준비를 하세요
🚗 운전: 수술 후 운전이 불가능하니 교통편을 미리 준비하세요

${getSurgeryPreparationItems(type)}

🚫 금지사항: 음주, 흡연, 과식, 밤샘`;
    } else {
      return `수술 당일:

• 수술 8시간 전부터 음식 섭취를 중단하세요 (물은 2시간 전까지 소량 가능)
• 화장품, 로션, 향수 등을 사용하지 마세요
• 편안한 옷차림으로 병원에 방문하세요 (앞으로 여미는 옷 추천)
• 신분증과 보험 관련 서류를 지참하세요
• 수술 후 귀가 도움을 줄 보호자와 함께 방문하세요
• 처방전, 의사의 연락처, 주의사항 등을 챙기세요

🚗 운전: 수술 당일과 회복 기간 동안 운전은 절대 금지입니다 (택시나 대중교통 이용)

${getSurgeryPreparationItems(type)}

🚫 절대 금지사항: 음식물 섭취, 화장, 콘택트렌즈 착용, 음주`;
    }
  };
  
  // 수술 후 케어 메시지
  const getCareMessageAfterSurgery = (type: SurgeryType, dayDiff: number): string => {
    const getRecoveryTips = (type: SurgeryType): string => {
      switch (type) {
        case '코성형':
          return "빠른 회복 팁: 머리를 높게 하여 자세요, 얼음찜질을 규칙적으로 하세요, 비염약을 사용하세요, 코를 만지거나 닦지 마세요, 기침/재채기할 때는 입을 벌리세요";
        case '눈성형':
          return "빠른 회복 팁: 냉찜질을 규칙적으로 하세요, 눈을 비비지 마세요, 충분한 휴식을 취하세요, 처방 안약을 사용하세요, 눈 사용을 최소화하세요";
        case '안면윤곽':
          return "빠른 회복 팁: 압박 붕대를 지시대로 착용하세요, 얼음찜질을 규칙적으로 하세요, 구강 세정제로 입안을 깨끗하게 유지하세요, 머리를 높게 하여 자세요, 빨대를 사용하세요";
        case '가슴성형':
          return "빠른 회복 팁: 지시된 압박 브라를 착용하세요, 상체를 높게 하여 자세요, 팔을 과도하게 움직이지 마세요, 처방된 약을 정확히 복용하세요, 수분을 충분히 섭취하세요";
        case '지방이식':
          return "빠른 회복 팁: 공여 부위에 압박 의류를 착용하세요, 이식 부위를 만지지 마세요, 충분한 단백질을 섭취하세요, 물을 많이 마시세요, 급격한 체중 변화를 피하세요";
        default:
          return "빠른 회복 팁: 충분한 휴식을 취하세요, 처방된 약을 정확히 복용하세요, 충분한 수분을 섭취하세요, 의사의 지시를 철저히 따르세요";
      }
    };

    const getNeverDoList = (type: SurgeryType): string => {
      switch (type) {
        case '코성형':
          return "절대 금지사항: 코를 건드리거나 만지지 마세요, 안경을 착용하지 마세요, 음주/흡연을 하지 마세요, 과도한 표정 짓기를 피하세요, 과격한 운동은 하지 마세요";
        case '눈성형':
          return "절대 금지사항: 눈을 비비거나 만지지 마세요, 콘택트렌즈 착용을 피하세요, 음주/흡연을 하지 마세요, 자외선에 노출되지 마세요, 눈 화장을 하지 마세요";
        case '안면윤곽':
          return "절대 금지사항: 얼굴을 누르거나 마사지하지 마세요, 단단한 음식을 씹지 마세요, 음주/흡연을 하지 마세요, 과격한 표정을 짓지 마세요, 입을 크게 벌리지 마세요, 말을 많이 하지 마세요";
        case '가슴성형':
          return "절대 금지사항: 엎드려 자지 마세요, 팔을 어깨 위로 들지 마세요, 무거운 물건을 들지 마세요, 음주/흡연을 하지 마세요, 브라 없이 지내지 마세요, 가슴을 누르거나 마사지하지 마세요";
        case '지방이식':
          return "절대 금지사항: 이식 부위를 누르거나 마사지하지 마세요, 음주/흡연을 하지 마세요, 다이어트나 체중 감량을 시도하지 마세요, 과격한 운동은 피하세요, 이식 부위에 압력을 가하지 마세요";
        default:
          return "절대 금지사항: 의사의 지시 없이 약을 복용하지 마세요, 음주/흡연을 하지 마세요, 과격한 운동은 피하세요, 상처를 긁거나 만지지 마세요";
      }
    };
    
    switch (type) {
      case '코성형':
        if (dayDiff <= 1) {
          return `수술 후 1일차:

• 얼음찜질을 20분씩 2시간 간격으로 하세요
• 필요시 처방된 진통제를 복용하세요
• 머리를 높게 하여 자세요 (최소 30도 이상)
• 필요 이상으로 말하거나 웃는 것을 피하세요
• 부기 감소를 위해 물을 많이 마시세요

🥤 음료: 물만 마시세요. 카페인, 알코올은 절대 금지
🍽️ 식사: 차갑고 부드러운 음식을 드세요
🏋️ 운동: 모든 운동 금지
👨‍💼 업무: 휴식을 취해야 합니다
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 3) {
          return `수술 후 2-3일차:

• 얼음찜질을 계속하세요 (부기가 가장 심한 시기)
• 코 주변 멍이 나타날 수 있습니다
• 출혈이나 분비물이 있을 수 있으니 거즈를 수시로 교체하세요
• 머리를 높게 하여 자세요
• 코를 풀거나 만지지 마세요

🥤 음료: 물, 차(따뜻하지 않은) 가능. 알코올, 카페인 금지
🍽️ 식사: 부드러운 음식을 드세요
🏋️ 운동: 모든 운동 금지
👨‍💼 업무: 재택근무만 가능
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 7) {
          return `수술 후 4-7일차:

• 이 시기에 코 부목이나 테이프를 제거할 수 있습니다 (의사 지시에 따름)
• 부기가 서서히 감소합니다
• 머리를 높게 유지하며 자세요
• 사우나, 온탕 등 열에 노출되는 것을 피하세요
• 안경 착용은 아직 피하세요

🥤 음료: 알코올, 매운 차는 계속 금지
🍽️ 식사: 일반 식사 가능하나 뜨거운 음식은 피하세요
🏋️ 운동: 가벼운 걷기 가능, 그 외 운동 금지
👨‍💼 업무: 가벼운 업무 가능
🚗 운전: 약물 복용 중이라면 운전 금지
🧳 여행: 가까운 지역 여행 가능
✈️ 비행: 짧은 비행(2시간 미만) 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 14) {
          return `수술 후 1-2주차:

• 수술 부위의 멍이 옅어집니다
• 부기는 계속 감소하지만 아직 남아있을 수 있습니다
• 코 내부 세척을 시작할 수 있습니다 (의사 지시에 따름)
• 가벼운 화장이 가능합니다
• 처방된 연고나 크림을 사용하세요

🥤 음료: 가벼운 알코올 소량 가능 (의사와 상담)
🍽️ 식사: 정상적인 식사 가능
🏋️ 운동: 가벼운 유산소 운동 가능, 고강도 운동 금지
👨‍💼 업무: 일반 업무 복귀 가능
🚗 운전: 약물 복용 중단 시 운전 가능
🧳 여행: 여행 가능
✈️ 비행: 중거리 비행 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 30) {
          return `수술 후 2-4주차:

• 부기가 대부분 사라집니다
• 약간의 코 숨막힘이 있을 수 있습니다
• 코 마사지를 시작할 수 있습니다 (의사 지시에 따름)
• 가벼운 운동을 시작할 수 있습니다
• 선크림을 꼭 바르세요

🥤 음료: 일반적인 음료 가능, 과도한 알코올 주의
🍽️ 식사: 정상적인 식사
🏋️ 운동: 중강도 운동 가능, 고강도 및 접촉 운동 주의
👨‍💼 업무: 정상 업무 가능
🚗 운전: 가능
🧳 여행: 가능
✈️ 비행: 장거리 비행 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else {
          return `수술 후 1개월 이상:

• 대부분의 부기와 멍이 사라집니다
• 코 모양이 안정화되기 시작합니다
• 대부분의 일상 활동이 가능합니다
• 자외선 차단제를 철저히 발라 흉터 예방하세요
• 코 모양의 미세한 변화는 6-12개월까지 계속됩니다

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 대부분 가능, 접촉 운동은 3개월 후
👨‍💼 업무: 제한 없음
🚗 운전: 제한 없음
🧳 여행: 제한 없음
✈️ 비행: 제한 없음

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        }
        
      case '눈성형':
        if (dayDiff <= 1) {
          return `수술 후 1일차:

• 냉찜질을 20분씩 1시간 간격으로 하세요
• 눈을 만지거나 비비지 마세요
• 처방된 안약을 정확히 사용하세요
• 눈의 사용을 최소화하세요 (TV, 핸드폰, 책 금지)
• 머리를 높게 하여 자세요

🥤 음료: 물만 마시세요. 카페인, 알코올은 절대 금지
🍽️ 식사: 소화가 잘 되는 부드러운 음식 섭취
🏋️ 운동: 모든 운동 금지
👨‍💼 업무: 휴식 필수
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 3) {
          return `수술 후 2-3일차:

• 냉찜질을 계속하세요
• 부기와 멍이 가장 심한 시기입니다
• 처방된 안약과 약물을 계속 사용하세요
• 콘택트렌즈 착용을 피하세요
• 실외 활동시 선글라스를 착용하세요

🥤 음료: 물, 차(따뜻하지 않은) 가능. 알코올, 카페인 금지
🍽️ 식사: 일반적인 음식 가능
🏋️ 운동: 모든 운동 금지
👨‍💼 업무: 재택근무만 가능
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 7) {
          return `수술 후 4-7일차:

• 멍과 부기가 서서히 줄어듭니다
• 눈의 건조함이 느껴질 수 있으니 인공눈물을 사용하세요
• 짧은 시간 동안 TV나 핸드폰 사용이 가능합니다
• 직사광선을 피하고 항상 선글라스를 착용하세요
• 눈 주변 흉터 케어를 시작할 수 있습니다

🥤 음료: 알코올, 카페인 소량 가능
🍽️ 식사: 제한 없음
🏋️ 운동: 가벼운 걷기 가능, 그 외 운동 금지
👨‍💼 업무: 가벼운 업무 가능
🚗 운전: 약물 복용 중이라면 운전 금지
🧳 여행: 가까운 지역 여행 가능
✈️ 비행: 짧은 비행(2시간 미만) 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 14) {
          return `수술 후 1-2주차:

• 대부분의 부기와 멍이 사라집니다
• 가벼운 눈 화장이 가능합니다 (아이섀도우, 마스카라 주의)
• 일상 활동을 서서히 재개할 수 있습니다
• 흉터 관리를 계속하세요
• 눈 주변 마사지를 시작할 수 있습니다 (의사와 상담)

🥤 음료: 일반적인 음료 가능
🍽️ 식사: 제한 없음
🏋️ 운동: 가벼운 유산소 운동 가능, 고강도 운동 금지
👨‍💼 업무: 일반 업무 복귀 가능
🚗 운전: 약물 복용 중단 시 운전 가능
🧳 여행: 여행 가능
✈️ 비행: 중거리 비행 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else {
          return `수술 후 2주 이상:

• 실밥이 모두 제거되었습니다
• 부기는 계속 감소하며 결과가 나타나기 시작합니다
• 일반적인 화장이 가능합니다
• 대부분의 일상 활동이 가능합니다
• 선크림을 꼭 사용하여 흉터 보호하세요

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 대부분 가능, 격렬한 운동은 의사와 상담
👨‍💼 업무: 제한 없음
🚗 운전: 제한 없음
🧳 여행: 제한 없음
✈️ 비행: 제한 없음

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        }
        
      case '안면윤곽':
        if (dayDiff <= 1) {
          return `안면윤곽 수술 후 1일차:

• 압박 붕대를 지시대로 꼭 착용하세요
• 얼음찜질을 20분씩 1시간 간격으로 하세요
• 처방된 진통제와 항생제를 정확히 복용하세요
• 머리를 높게 하여 자세요 (부기 감소에 도움)
• 입안의 상처를 확인하고 처방된 구강 세정제로 관리하세요

🥤 음료: 물만 마시세요 (빨대 사용 금지). 알코올, 카페인 절대 금지
🍽️ 식사: 차가운 죽, 부드러운 음식만 섭취 (씹지 말고 삼키세요)
🏋️ 운동: 모든 운동 절대 금지
👨‍💼 업무: 휴식 필수
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 3) {
          return `안면윤곽 수술 후 2-3일차:

• 부기가 가장 심한 시기입니다 (정상)
• 압박 붕대를 계속 착용하세요
• 얼음찜질을 계속하세요
• 입안 상처 관리에 신경쓰세요
• 얼굴을 만지거나 마사지하지 마세요

🥤 음료: 물, 미지근한 차 가능 (빨대 사용 금지)
🍽️ 식사: 부드러운 음식만 섭취, 씹는 행동 최소화
🏋️ 운동: 모든 운동 금지
👨‍💼 업무: 재택근무만 가능 (말하는 것도 최소화)
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 7) {
          return `안면윤곽 수술 후 4-7일차:

• 부기가 서서히 감소하기 시작합니다
• 압박 붕대는 의사의 지시에 따라 착용하세요
• 통증이 줄어들며 입안 불편함도 감소합니다
• 구강 위생에 계속 신경쓰세요
• 얼굴 표정을 지나치게 짓지 마세요

🥤 음료: 따뜻하지 않은 음료 가능
🍽️ 식사: 부드러운 음식 섭취, 약간의 씹기 가능
🏋️ 운동: 가벼운 걷기만 가능
👨‍💼 업무: 가벼운 재택근무 가능
🚗 운전: 약물 복용 중이라면 운전 금지
🧳 여행: 근거리만 가능
✈️ 비행: 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 14) {
          return `안면윤곽 수술 후 1-2주차:

• 부기가 크게 감소합니다 (약 40-50% 회복)
• 일부 실밥을 제거할 수 있습니다
• 입안 상처가 대부분 아물기 시작합니다
• 부드러운 음식 섭취가 가능해집니다
• 일상 활동을 조금씩 늘릴 수 있습니다

🥤 음료: 대부분의 음료 가능, 알코올은 소량만
🍽️ 식사: 부드러운 음식, 작게 자른 일반 음식 가능
🏋️ 운동: 가벼운 유산소 운동 가능, 고강도 운동 금지
👨‍💼 업무: 일반 업무 복귀 가능
🚗 운전: 약물 복용 중단 시 운전 가능
🧳 여행: 가능하나 장거리는 피하세요
✈️ 비행: 의사와 상담 필요

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 30) {
          return `안면윤곽 수술 후 2-4주차:

• 부기가 상당히 감소합니다 (약 60-70% 회복)
• 대부분의 실밥이 제거됩니다
• 약간의 감각 저하나 둔함이 있을 수 있습니다
• 부드러운 안면 마사지를 시작할 수 있습니다 (의사와 상담)
• 선크림을 반드시 바르세요

🥤 음료: 제한 없음, 알코올은 적당히
🍽️ 식사: 대부분의 음식 가능, 아주 딱딱한 음식은 주의
🏋️ 운동: 중강도 운동 가능, 격렬한 운동은 의사와 상담
👨‍💼 업무: 제한 없음
🚗 운전: 가능
🧳 여행: 가능
✈️ 비행: 의사와 상담 후 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 90) {
          return `안면윤곽 수술 후 1-3개월:

• 부기가 대부분 사라집니다 (약 80-90% 회복)
• 얼굴 윤곽이 서서히 자리잡기 시작합니다
• 감각이 점차 회복됩니다
• 흉터 관리에 계속 신경쓰세요
• 대부분의 활동이 가능합니다

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 대부분 가능, 접촉 운동은 의사와 상담
👨‍💼 업무: 제한 없음
🚗 운전: 제한 없음
🧳 여행: 제한 없음
✈️ 비행: 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else {
          return `안면윤곽 수술 후 3개월 이상:

• 최종 결과가 나타나기 시작합니다
• 미세한 부기나 변화는 6-12개월까지 계속될 수 있습니다
• 정상적인 감각이 대부분 회복됩니다
• 모든 일상 활동이 가능합니다
• 정기 검진을 잊지 마세요

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 제한 없음
👨‍💼 업무: 제한 없음
🚗 운전: 제한 없음
🧳 여행: 제한 없음
✈️ 비행: 제한 없음

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        }
      
      case '가슴성형':
        if (dayDiff <= 1) {
          return `가슴성형 수술 후 1일차:

• 압박 브라를 지시대로 꼭 착용하세요
• 처방된 진통제와 항생제를 정확히 복용하세요
• 수술 부위에 냉찜질을 20분씩 1시간 간격으로 하세요
• 팔을 어깨 위로 들어올리지 마세요
• 측면이나 등으로 눕지 말고 반드시 반좌위로 휴식하세요

🥤 음료: 물만 마시세요. 카페인, 알코올은 절대 금지
🍽️ 식사: 소화가 잘 되는 부드러운 음식 섭취
🏋️ 운동: 모든 운동 절대 금지
👨‍💼 업무: 휴식 필수
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 3) {
          return `가슴성형 수술 후 2-3일차:

• 부기와 불편함이 최고조에 달하는 시기입니다
• 압박 브라를 계속 착용하세요 (24시간)
• 처방된 약을 정확히 복용하세요
• 가능한 반좌위 자세를 유지하세요
• 팔 사용을 최소화하세요

🥤 음료: 물, 차(따뜻하지 않은) 가능. 카페인 주의, 알코올 금지
🍽️ 식사: 일반적인 음식 가능
🏋️ 운동: 모든 운동 금지
👨‍💼 업무: 휴식 권장, 가벼운 재택근무만 가능
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 7) {
          return `가슴성형 수술 후 4-7일차:

• 부기가 서서히 감소하기 시작합니다
• 압박 브라는 계속 착용하세요 (의사 지시에 따름)
• 가벼운 활동이 가능해집니다
• 흉터 관리에 신경쓰세요
• 천천히 움직이고 갑작스러운 동작을 피하세요

🥤 음료: 카페인 소량 가능, 알코올 금지
🍽️ 식사: 제한 없음, 단백질 섭취 권장
🏋️ 운동: 가벼운 걷기만 가능
👨‍💼 업무: 가벼운 업무 가능 (팔 사용 최소화)
🚗 운전: 금지
🧳 여행: 근거리만 가능
✈️ 비행: 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 14) {
          return `가슴성형 수술 후 1-2주차:

• 부기가 크게 감소합니다
• 압박 브라 착용 일정을 의사와 상담하세요
• 흉터 관리를 적극적으로 시작하세요
• 가슴 마사지를 시작할 수 있습니다 (의사 지시에 따름)
• 일상 활동을 서서히 늘리세요

🥤 음료: 대부분 가능, 알코올 소량만
🍽️ 식사: 제한 없음
🏋️ 운동: 가벼운 유산소 운동 가능, 상체 운동 금지
👨‍💼 업무: 일반 업무 복귀 가능
🚗 운전: 의사와 상담 후 가능
🧳 여행: 가능하나 장거리는 피하세요
✈️ 비행: 의사와 상담 필요

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 30) {
          return `가슴성형 수술 후 2-4주차:

• 부기가 상당히 감소합니다
• 정상적인 브라 착용 가능 시기인지 의사와 상담하세요
• 흉터 관리를 계속하세요
• 일상 활동이 대부분 가능합니다
• 가슴의 모양이 서서히 자리잡기 시작합니다

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 중강도 운동 가능, 상체 운동은 의사와 상담
👨‍💼 업무: 정상 업무 가능
🚗 운전: 가능
🧳 여행: 가능
✈️ 비행: 단거리 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 90) {
          return `가슴성형 수술 후 1-3개월:

• 부기가 대부분 사라집니다
• 가슴 윤곽이 더 자연스러워집니다
• 대부분의 활동이 가능합니다
• 상체 운동을 서서히 시작할 수 있습니다
• 흉터 관리를 계속하세요

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 대부분 가능, 고강도 운동은 의사와 상담
👨‍💼 업무: 제한 없음
🚗 운전: 제한 없음
🧳 여행: 제한 없음
✈️ 비행: 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else {
          return `가슴성형 수술 후 3개월 이상:

• 최종 결과가 나타나기 시작합니다
• 미세한 변화는 6-12개월까지 계속될 수 있습니다
• 정상적인 활동이 모두 가능합니다
• 정기 검진을 잊지 마세요
• 1년에 한 번 이상 검진을 받는 것이 좋습니다

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 제한 없음
👨‍💼 업무: 제한 없음
🚗 운전: 제한 없음
🧳 여행: 제한 없음
✈️ 비행: 제한 없음

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        }
        
      case '지방이식':
        if (dayDiff <= 1) {
          return `지방이식 수술 후 1일차:

• 압박 의류를 지시대로 꼭 착용하세요
• 이식 부위는 압박하지 마세요
• 처방된 진통제와 항생제를 정확히 복용하세요
• 이식 부위를 만지거나 마사지하지 마세요
• 충분한 수분을 섭취하세요

🥤 음료: 물을 많이 마시세요. 카페인, 알코올은 절대 금지
🍽️ 식사: 소화가 잘 되는 부드러운 음식 섭취
🏋️ 운동: 모든 운동 절대 금지
👨‍💼 업무: 휴식 필수
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 3) {
          return `지방이식 수술 후 2-3일차:

• 부기와 멍이 심한 시기입니다
• 공여 부위는 압박 의류를 계속 착용하세요
• 이식 부위는 압박하지 마세요
• 많은 휴식을 취하세요
• 충분한 수분을 섭취하세요

🥤 음료: 물을 많이 마시세요. 카페인, 알코올 금지
🍽️ 식사: 일반적인 음식 가능, 수분과 단백질 섭취 권장
🏋️ 운동: 모든 운동 금지
👨‍💼 업무: 재택근무만 가능
🚗 운전: 약물 복용 중이라면 운전 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 7) {
          return `지방이식 수술 후 4-7일차:

• 부기가 서서히 감소하기 시작합니다
• 공여 부위 압박 의류는 계속 착용하세요
• 이식 부위를 만지거나 마사지하지 마세요
• 흡연과 음주를 절대 피하세요
• 가벼운 활동이 가능해집니다

🥤 음료: 물을 많이 마시세요. 카페인 소량 가능, 알코올 금지
🍽️ 식사: 제한 없음, 수분과 단백질 섭취 권장
🏋️ 운동: 가벼운 걷기만 가능
👨‍💼 업무: 가벼운 업무 가능
🚗 운전: 약물 복용 중단 시 운전 가능
🧳 여행: 근거리만 가능
✈️ 비행: 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 14) {
          return `지방이식 수술 후 1-2주차:

• 부기가 크게 감소합니다
• 공여 부위 압박 의류 착용 일정 확인하세요
• 이식 부위 마사지는 아직 피하세요
• 일상 활동을 서서히 늘리세요
• 흡연과 음주를 계속 피하세요

🥤 음료: 물을 충분히 마시세요. 카페인 가능, 알코올 소량만
🍽️ 식사: 제한 없음
🏋️ 운동: 가벼운 유산소 운동 가능, 고강도 운동 금지
👨‍💼 업무: 일반 업무 복귀 가능
🚗 운전: 가능
🧳 여행: 가능하나 장거리는 피하세요
✈️ 비행: 의사와 상담 필요

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 30) {
          return `지방이식 수술 후 2-4주차:

• 부기가 상당히 감소합니다
• 이식된 지방의 초기 생존율이 결정되는 시기입니다
• 정상적인 활동이 대부분 가능합니다
• 이식 부위 마사지 가능 여부를 의사와 상담하세요
• 체중 변화를 피하세요

🥤 음료: 제한 없음, 물을 충분히 마시세요
🍽️ 식사: 제한 없음, 건강한 식단 유지
🏋️ 운동: 중강도 운동 가능, 고강도는 의사와 상담
👨‍💼 업무: 정상 업무 가능
🚗 운전: 가능
🧳 여행: 가능
✈️ 비행: 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else {
          return `지방이식 수술 후 1개월 이상:

• 이식된 지방이 안정화되기 시작합니다
• 최종 결과는 3-6개월 후 확인 가능합니다
• 모든 일상 활동이 가능합니다
• 급격한 체중 변화를 피하세요
• 정기 검진을 통해 결과를 확인하세요

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음, 건강한 식단 유지
🏋️ 운동: 제한 없음
👨‍💼 업무: 제한 없음
🚗 운전: 제한 없음
🧳 여행: 제한 없음
✈️ 비행: 제한 없음

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        }
      
      default:
        if (dayDiff <= 1) {
          return `수술 후 1일차:

• 의사가 처방한 약을 정확히 복용하세요
• 수술 부위에 냉찜질을 규칙적으로 하세요
• 과도한 움직임을 피하고 충분히 쉬세요
• 수술 부위를 깨끗하고 건조하게 유지하세요
• 탈수 방지를 위해 물을 충분히 마시세요

🥤 음료: 물만 마시세요. 카페인, 알코올은 절대 금지
🍽️ 식사: 소화가 잘 되는 부드러운 음식 섭취
🏋️ 운동: 모든 운동 금지
👨‍💼 업무: 휴식 필수
🚗 운전: 절대 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 3) {
          return `수술 후 2-3일차:

• 부기와 불편함이 최고조에 달하는 시기입니다
• 냉찜질을 계속하세요
• 처방된 약을 정확히 복용하세요
• 수술 부위 관리 지침을 철저히 따르세요
• 충분한 수분을 섭취하세요

🥤 음료: 물, 차(따뜻하지 않은) 가능. 알코올, 카페인 금지
🍽️ 식사: 일반적인 음식 가능하나 소화가 잘 되는 음식 권장
🏋️ 운동: 모든 운동 금지
👨‍💼 업무: 재택근무만 가능
🚗 운전: 약물 복용 중이라면 운전 금지
🧳 여행: 절대 금지
✈️ 비행: 절대 금지

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 7) {
          return `수술 후 4-7일차:

• 부기가 서서히 감소하기 시작합니다
• 일부 실밥을 제거할 수 있습니다 (의사와 상담)
• 가벼운 일상 활동을 시작할 수 있습니다
• 상처 부위를 청결하게 유지하세요
• 흉터 관리를 시작할 수 있습니다

🥤 음료: 알코올, 카페인 소량 가능 (의사와 상담)
🍽️ 식사: 제한 없음
🏋️ 운동: 가벼운 걷기 가능, 그 외 운동 금지
👨‍💼 업무: 가벼운 업무 가능
🚗 운전: 약물 복용 중단 시 운전 가능
🧳 여행: 가까운 지역 여행 가능
✈️ 비행: 의사와 상담 필요

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 14) {
          return `수술 후 1-2주차:

• 대부분의 부기가 감소합니다
• 남은 실밥을 제거합니다
• 일상 활동으로 점차 복귀할 수 있습니다
• 흉터 관리를 지속하세요
• 처방된 연고나 크림을 사용하세요

🥤 음료: 일반적인 음료 가능
🍽️ 식사: 제한 없음
🏋️ 운동: 가벼운 유산소 운동 가능, 고강도 운동 금지
👨‍💼 업무: 일반 업무 복귀 가능
🚗 운전: 약물 복용 중단 시 운전 가능
🧳 여행: 여행 가능
✈️ 비행: 중거리 비행 가능 (의사와 상담)

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else if (dayDiff <= 30) {
          return `수술 후 2-4주차:

• 부기가 대부분 사라집니다
• 흉터가 점차 옅어지기 시작합니다
• 대부분의 일상 활동이 가능합니다
• 흉터 관리를 지속하세요
• 선크림을 꼭 바르세요

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 중강도 운동 가능, 고강도 운동은 의사와 상담
👨‍💼 업무: 정상 업무 가능
🚗 운전: 가능
🧳 여행: 가능
✈️ 비행: 대부분 가능

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        } else {
          return `수술 후 1개월 이상:

• 대부분의 부기와 불편함이 사라집니다
• 흉터가 옅어지고 있습니다 (완전히 사라지기까지 6-12개월 소요)
• 거의 모든 활동이 가능합니다
• 정기 검진을 통해 회복 상태를 확인하세요
• 흉터 관리와 자외선 차단을 지속하세요

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 대부분 가능, 접촉 운동은 의사와 상담
👨‍💼 업무: 제한 없음
🚗 운전: 제한 없음
🧳 여행: 제한 없음
✈️ 비행: 제한 없음

${getRecoveryTips(type)}

${getNeverDoList(type)}`;
        }
    }
  };
  
  // 상담일 케어 메시지
  const getCareMessageForConsultation = (type: SurgeryType): string => {
    const generalPrep = `상담 준비사항:

• 건강 상태와 복용 중인 약물 목록을 준비하세요
• 이전 수술 경험이 있다면 관련 기록을 지참하세요
• 알레르기가 있다면 미리 알려주세요
• 원하는 결과에 대한 참고 이미지를 준비하세요
• 궁금한 점을 미리 메모해두세요 (회복 기간, 위험성, 비용 등)
• 보험 관련 서류가 필요하다면 준비하세요

질문 목록 예시:
1. 수술 후 회복 기간은 얼마나 되나요?
2. 어떤 부작용이 있을 수 있나요?
3. 수술 후 일상생활 복귀는 언제부터 가능한가요?
4. 흉터는 어떻게 관리해야 하나요?
5. 수술 후 어떤 증상이 나타나면 병원에 연락해야 하나요?

👨‍💼 업무: 상담 후 바로 업무 복귀 가능
🚗 운전: 가능
🧳 여행: 가능
✈️ 비행: 가능`;

    switch (type) {
      case '코성형':
        return `코성형 상담일:

${generalPrep}

코성형 특화 질문:
1. 코 막힘이나 호흡 문제가 발생할 수 있나요?
2. 어떤 종류의 임플란트/재료를 사용하나요?
3. 코 모양이 안정화되는 시점은 언제인가요?
4. 안경이나 선글라스는 언제부터 착용 가능한가요?
5. 이비인후과 질환이 있을 경우 영향이 있나요?`;

      case '눈성형':
        return `눈성형 상담일:

${generalPrep}

눈성형 특화 질문:
1. 수술 후 시력에 영향이 있나요?
2. 눈이 완전히 감기지 않는 문제가 있을 수 있나요?
3. 안구 건조증이 생길 가능성은 있나요?
4. 콘택트렌즈는 언제부터 착용 가능한가요?
5. 부기가 완전히 가라앉는 시기는 언제인가요?`;
      
      case '안면윤곽':
        return `안면윤곽 수술 상담일:

${generalPrep}

안면윤곽 특화 질문:
1. 어떤 수술 방법을 사용하나요? (골절술, 절골술, 임플란트 등)
2. 입안에 상처가 생기나요? 구강 관리는 어떻게 해야 하나요?
3. 얼굴 감각이 영구적으로 변할 가능성이 있나요?
4. 씹는 기능에 영향이 있을 수 있나요?
5. 부기가 완전히 가라앉는 시기는 언제인가요?
6. 보형물을 사용한다면 재질과 내구성은 어떤가요?`;
      
      case '가슴성형':
        return `가슴성형 상담일:

${generalPrep}

가슴성형 특화 질문:
1. 어떤 종류의 보형물을 사용하나요? (재질, 모양, 크기)
2. 보형물의 수명과 교체 시기는 언제인가요?
3. 수술 후 감각 변화나 수유 가능성에 영향이 있나요?
4. 보형물 위치는 어디인가요? (근육 위, 근육 아래)
5. 압박 브라는 얼마나 오래 착용해야 하나요?
6. 상체 운동은 언제부터 가능한가요?`;
      
      case '지방이식':
        return `지방이식 상담일:

${generalPrep}

지방이식 특화 질문:
1. 지방을 채취하는 부위는 어디인가요?
2. 이식된 지방의 생존율은 얼마나 되나요?
3. 체중 변화가 결과에 영향을 미치나요?
4. 여러 번의 시술이 필요할 가능성이 있나요?
5. 공여 부위와 이식 부위 모두 압박 의류를 착용해야 하나요?
6. 최종 결과는 언제 확인할 수 있나요?`;

      default:
        return `${type} 상담일:

${generalPrep}

${type} 특화 질문:
1. 수술 결과가 완전히 나타나는 시기는 언제인가요?
2. 흉터 관리는 어떻게 해야 하나요?
3. 일상생활 복귀는 언제부터 가능한가요?
4. 이후 추가 시술이 필요할 가능성은 있나요?
5. 수술 후 특별히 주의해야 할 사항은 무엇인가요?`;
    }
  };
  
  // 수술일 케어 메시지
  const getCareMessageForSurgeryDay = (type: SurgeryType): string => {
    const generalPrep = `수술 당일 준비사항:

• 수술 8시간 전부터 금식하세요 (물은 2시간 전까지 소량 가능)
• 화장품, 로션, 향수 등을 사용하지 마세요
• 콘택트렌즈, 액세서리, 매니큐어를 모두 제거하세요
• 편안한 앞여밈 상의와 느슨한 바지를 착용하세요
• 수술 후 귀가 도움을 줄 보호자와 함께 방문하세요
• 귀중품은 집에 두고 오세요
• 필요한 서류와 신분증을 지참하세요

🥤 음료: 수술 8시간 전부터 금지 (물은 2시간 전까지 소량 가능)
🍽️ 식사: 수술 8시간 전부터 금지
🚗 운전: 수술 후 운전 불가능 (귀가 교통편 필요)
🧳 여행: 수술 직후 여행 금지
✈️ 비행: 수술 직후 비행 금지`;

    switch (type) {
      case '코성형':
        return `코성형 수술 당일:

${generalPrep}

코성형 당일 특화 준비물:
• 콘택트렌즈 대신 안경을 지참하세요
• 목 베개나 U자형 여행용 베개
• 입술 보습제 (코로 숨쉬기 어려울 수 있음)
• 작은 거즈나 티슈
• 차가운 얼음팩`;

      case '눈성형':
        return `눈성형 수술 당일:

${generalPrep}

눈성형 당일 특화 준비물:
• 콘택트렌즈 대신 안경을 지참하세요
• 선글라스 (수술 후 외출시 필요)
• 인공눈물
• 차가운 얼음팩
• 눈을 가릴 수 있는 수면 안대`;

      case '안면윤곽':
        return `안면윤곽 수술 당일:

${generalPrep}

안면윤곽 당일 특화 준비물:
• 앞여밈 상의와 느슨한 바지
• 압박 붕대 (병원에서 제공하기도 함)
• 얼음팩
• 빨대 (액체 섭취용)
• 구강 세정제
• 입술 보습제
• 부드러운 음식과 음료 (죽, 미음 등)`;

      case '가슴성형':
        return `가슴성형 수술 당일:

${generalPrep}

가슴성형 당일 특화 준비물:
• 앞여밈 상의 (단추나 지퍼)
• 압박 브라 (병원에서 제공하기도 함)
• 베개 여러 개 (반좌위 자세 유지용)
• 얼음팩
• 영양제 (의사 상담 후)
• 밴드 에이드`;

      case '지방이식':
        return `지방이식 수술 당일:

${generalPrep}

지방이식 당일 특화 준비물:
• 압박 의류 (공여 부위용)
• 느슨한 옷 (이식 부위에 압박이 없는)
• 작은 베개 (이식 부위 보호용)
• 얼음팩 (공여 부위용)
• 진통제
• 항생제`;

      default:
        return `${type} 수술 당일:

${generalPrep}

${type} 당일 특화 준비물:
• 편안한 앞여밈 옷
• 압박 의류 (의사가 권장한 경우)
• 진통제
• 냉찜질팩
• 의사가 추천한 특화 관리 용품`;
    }
  };
  
  // 실밥제거일 케어 메시지
  const getCareMessageForStitchRemoval = (type: SurgeryType): string => {
    const generalInfo = `실밥 제거 당일:

• 실밥 제거는 일반적으로 통증이 거의 없거나 약간의 불편함만 있습니다
• 세안이나 샤워 전에 방문하는 것이 좋습니다
• 실밥 제거 후 의사의 지시에 따라 흉터 관리를 시작하세요
• 실밥 제거 후 흉터 크림이나 실리콘 시트 사용법을 문의하세요
• 향후 관리 계획과 후속 방문 일정을 확인하세요

🥤 음료: 제한 없음
🍽️ 식사: 제한 없음
🏋️ 운동: 가벼운 활동 가능, 격렬한 운동은 의사와 상담
👨‍💼 업무: 가능
🚗 운전: 가능
🧳 여행: 장거리 여행은 의사와 상담
✈️ 비행: 의사와 상담 필요`;

    switch (type) {
      case '코성형':
        return `코성형 실밥 제거일:

${generalInfo}

코성형 실밥 제거 후 특별 관리:
• 코 내부 실밥 제거는 약간의 불편함이 있을 수 있습니다
• 코 내부 세척법을 정확히 배우세요
• 실밥 제거 후 며칠간 코 내부에 출혈이나 분비물이 있을 수 있습니다
• 안경 착용 가능 시기를 확인하세요
• 코 마사지나 테이핑 방법을 배우세요

회복 촉진 팁:
• 코 내부 보습을 위해 식염수 스프레이 사용
• 코를 만지거나 누르지 마세요
• 머리를 높게 하여 자세요
• 자외선 차단제를 꼼꼼히 발라 흉터 예방하세요`;

      case '눈성형':
        return `눈성형 실밥 제거일:

${generalInfo}

눈성형 실밥 제거 후 특별 관리:
• 실밥 제거는 약간의 따끔거림이 있을 수 있습니다
• 눈 주변 마사지나 테이핑 방법을 배우세요
• 눈 화장 가능 시기를 확인하세요
• 콘택트렌즈 착용 가능 시기를 확인하세요
• 자외선 차단을 철저히 하세요

회복 촉진 팁:
• 인공눈물로 건조함 예방
• 눈을 비비거나 만지지 마세요
• 컴퓨터나 스마트폰 사용 시간 제한
• 충분한 수면 취하기`;

      case '안면윤곽':
        return `안면윤곽 실밥 제거일:

${generalInfo}

안면윤곽 실밥 제거 후 특별 관리:
• 입 안 실밥 제거는 약간의 불편함이 있을 수 있습니다
• 구강 세정제로 입 안을 깨끗하게 유지하세요
• 처방된 연고를 지시대로 사용하세요
• 얼굴 감각이 완전히 돌아오기까지 시간이 걸릴 수 있습니다
• 씹는 기능이 회복되는 동안 부드러운 음식을 섭취하세요

회복 촉진 팁:
• 입 안 위생을 철저히 유지하세요
• 얼굴 마사지는 의사와 상담 후 시작하세요
• 점진적으로 음식 종류를 늘려가세요
• 과도한 입 벌림이나 말하기를 피하세요
• 자외선 차단제를 꼼꼼히 발라 흉터 예방하세요`;

      case '가슴성형':
        return `가슴성형 실밥 제거일:

${generalInfo}

가슴성형 실밥 제거 후 특별 관리:
• 압박 브라 착용 기간을 정확히 확인하세요
• 흉터 케어 방법을 배우세요 (실리콘 시트, 흉터 크림 등)
• 가슴 마사지 방법과 시작 시기를 확인하세요
• 팔 운동 제한 기간을 확인하세요
• 보형물 관련 주의사항을 숙지하세요

회복 촉진 팁:
• 처방된 흉터 케어 제품을 사용하세요
• 과도한 팔 움직임을 피하세요
• 상체를 높이고 자세요
• 가슴에 직접적인 압력이 가해지지 않도록 주의하세요
• 규칙적인 가슴 마사지로 보형물 캡슐화 예방 (의사 지시에 따름)`;

      case '지방이식':
        return `지방이식 실밥 제거일:

${generalInfo}

지방이식 실밥 제거 후 특별 관리:
• 공여 부위 흉터 케어 방법을 배우세요
• 이식 부위 압박 주의사항을 확인하세요
• 공여 부위 압박 의류 착용 기간을 확인하세요
• 이식 부위 마사지 가능 여부를 확인하세요
• 체중 관리의 중요성을 인지하세요

회복 촉진 팁:
• 흉터 케어 제품을 사용하세요
• 충분한 수분과 영양 섭취로 이식된 지방의 생존율을 높이세요
• 급격한 체중 변화를 피하세요
• 지방이식 부위에 직접적인 압력을 가하지 마세요
• 자외선 차단제를 꼼꼼히 발라 흉터 예방하세요`;

      default:
        return `${type} 실밥 제거일:

${generalInfo}

${type} 실밥 제거 후 특별 관리:
• 상처 부위를 깨끗하고 건조하게 유지하세요
• 흉터 케어 방법 (실리콘 시트, 흉터 크림 등)을 배우세요
• 압박 옷 착용 여부와 기간을 확인하세요
• 마사지 시작 시기와 방법을 확인하세요
• 자외선 차단을 철저히 하세요

회복 촉진 팁:
• 비타민이 풍부한 식단 유지
• 충분한 물 마시기
• 흡연 피하기
• 적절한 휴식과 점진적 활동 늘리기`;
    }
  };
  
  // 월 변경 핸들러
  const changeMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => 
      direction === 'prev' 
        ? prev.subtract(1, 'month') 
        : prev.add(1, 'month')
    );
  };

  // 날짜 색상 결정 함수
  const getDateColor = (dateStr: string) => {
    // 모든 스케줄에서 해당 날짜의 정보 확인
    for (const schedule of surgerySchedules) {
      if (dateStr === schedule.surgeryDate) return colors.highlight; // 수술일은 하이라이트 색상 (빨강)
    }
    
    // 선택된 스케줄의 상담일과 실밥제거일
    const selectedSchedule = surgerySchedules.find(schedule => schedule.id === selectedScheduleId);
    if (selectedSchedule) {
      if (dateStr === selectedSchedule.consultationDate) return colors.secondary;
      if (dateStr === selectedSchedule.stitchRemovalDate) return colors.primary;
    }
    
    return null;
  };
  
  // 특정 날짜에 여러 스케줄이 있는지 확인하는 함수
  const getSchedulesForDate = (dateStr: string) => {
    return surgerySchedules.filter(schedule => 
      schedule.consultationDate === dateStr || 
      schedule.surgeryDate === dateStr || 
      schedule.stitchRemovalDate === dateStr
    );
  };
  
  // 캘린더 렌더링
  return (
    <div className={`w-full ${isFullWidth ? 'max-w-full' : 'max-w-xl'} mx-auto`}>
      {currentView === 'calendar' ? (
        <div style={{ 
          position: 'relative',
          backgroundColor: isDarkMode ? '#1F2937' : '#F9FAFB',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          padding: '16px',
          minHeight: '600px'
        }}>
          {/* 헤더 */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            marginBottom: '20px'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '15px'
            }}>
              <h2 style={{ 
                fontSize: '22px', 
                fontWeight: 'bold',
                color: isDarkMode ? '#F9FAFB' : '#111827',
                marginBottom: '8px'
              }}>
                여러분의 안전한 여정을 Kuxy와 함께 만들어봐요
              </h2>
              <p style={{
                fontSize: '14px',
                color: isDarkMode ? '#D1D5DB' : '#6B7280'
              }}>
                맞춤형 회복 정보와 함께 건강한 성형 여정을 계획하세요
              </p>
            </div>
            
            {surgerySchedules.length > 0 && surgerySchedules.find(s => s.surgeryDate) && (
              <div style={{
                marginTop: '10px',
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    fontSize: '15px',
                    fontWeight: 'bold',
                    color: isDarkMode ? '#E5E7EB' : '#111827'
                  }}>
                    전체 회복 진행률
                  </span>
                  {(() => {
                    // 모든 수술 스케줄의 진행률 평균 계산
                    const validSchedules = surgerySchedules.filter(s => s.surgeryDate);
                    if (validSchedules.length === 0) return null;
                    
                    const today = dayjs();
                    let totalProgress = 0;
                    
                    // 각 수술별 진행률 계산 후 평균 구하기
                    for (const schedule of validSchedules) {
                      if (!schedule.surgeryDate) continue;
                      
                      const surgeryDate = dayjs(schedule.surgeryDate);
                      const daysSinceSurgery = today.diff(surgeryDate, 'day');
                      
                      // 회복 기간을 30일로 가정
                      const recoveryDays = 30;
                      let scheduleProgress = Math.min(Math.max(daysSinceSurgery, 0) / recoveryDays * 100, 100);
                      
                      // 수술 전이면 0%
                      if (daysSinceSurgery < 0) scheduleProgress = 0;
                      
                      totalProgress += scheduleProgress;
                    }
                    
                    // 평균 진행률 계산
                    let progress = totalProgress / validSchedules.length;
                    
                    return (
                      <span style={{
                        fontSize: '15px',
                        fontWeight: 'bold',
                        color: progress >= 100 ? '#10B981' : (progress > 50 ? '#3B82F6' : '#E83E8C')
                      }}>
                        {Math.round(progress)}%
                      </span>
                    );
                  })()}
                </div>
                
                <div style={{
                  width: '100%',
                  height: '12px',
                  backgroundColor: isDarkMode ? '#374151' : '#E5E7EB',
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}>
                  {(() => {
                    // 모든 수술 스케줄의 진행률 평균 계산
                    const validSchedules = surgerySchedules.filter(s => s.surgeryDate);
                    if (validSchedules.length === 0) return null;
                    
                    const today = dayjs();
                    let totalProgress = 0;
                    
                    // 각 수술별 진행률 계산 후 평균 구하기
                    for (const schedule of validSchedules) {
                      if (!schedule.surgeryDate) continue;
                      
                      const surgeryDate = dayjs(schedule.surgeryDate);
                      const daysSinceSurgery = today.diff(surgeryDate, 'day');
                      
                      // 회복 기간을 30일로 가정
                      const recoveryDays = 30;
                      let scheduleProgress = Math.min(Math.max(daysSinceSurgery, 0) / recoveryDays * 100, 100);
                      
                      // 수술 전이면 0%
                      if (daysSinceSurgery < 0) scheduleProgress = 0;
                      
                      totalProgress += scheduleProgress;
                    }
                    
                    // 평균 진행률 계산
                    let progress = totalProgress / validSchedules.length;
                    
                    // 진행률에 따른 색상 변경
                    const barColor = progress >= 100 
                      ? '#10B981' // 완료: 초록색
                      : progress > 75 
                        ? '#10B981' // 75% 이상: 초록색
                        : progress > 50 
                          ? '#3B82F6' // 50%-75%: 파란색
                          : progress > 25 
                            ? '#6366F1' // 25%-50%: 인디고색
                            : '#E83E8C'; // 0%-25%: 핑크색
                    
                    // 다중 수술 진행 표시
                    return (
                      <>
                        {/* 주 진행률 바 */}
                        <div 
                          style={{
                            width: `${progress}%`,
                            height: '100%',
                            backgroundColor: barColor,
                            borderRadius: '6px',
                            transition: 'width 0.5s ease-in-out',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {/* 개별 수술 마커 표시 (다중 수술인 경우) */}
                          {validSchedules.length > 1 && validSchedules.map((schedule, index) => {
                            if (!schedule.surgeryDate) return null;
                            
                            const surgeryDate = dayjs(schedule.surgeryDate);
                            const daysSinceSurgery = today.diff(surgeryDate, 'day');
                            
                            // 회복 기간을 30일로 가정
                            const recoveryDays = 30;
                            let scheduleProgress = Math.min(Math.max(daysSinceSurgery, 0) / recoveryDays * 100, 100);
                            
                            // 수술 전이면 표시 안함
                            if (daysSinceSurgery < 0) return null;
                            
                            // 수술 타입별 색상
                            const typeColor = 
                              schedule.type === '코성형' ? '#ef4444' : 
                              schedule.type === '눈성형' ? '#3b82f6' : 
                              schedule.type === '안면윤곽' ? '#8b5cf6' : 
                              schedule.type === '가슴성형' ? '#ec4899' : 
                              schedule.type === '지방이식' ? '#f59e0b' : '#10b981';
                            
                            // 특정 위치에 작은 수직선 표시
                            return (
                              <div
                                key={schedule.id}
                                style={{
                                  position: 'absolute',
                                  left: `${scheduleProgress}%`,
                                  top: 0,
                                  bottom: 0,
                                  width: '2px',
                                  backgroundColor: schedule.id === selectedScheduleId ? 'white' : 'rgba(255,255,255,0.5)',
                                  boxShadow: schedule.id === selectedScheduleId ? `0 0 4px ${typeColor}` : 'none',
                                  transform: 'translateX(-50%)',
                                  zIndex: 2
                                }}
                              />
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </div>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '6px',
                  fontSize: '12px',
                  color: isDarkMode ? '#9CA3AF' : '#6B7280',
                }}>
                  <span>수술일</span>
                  <span>1주차</span>
                  <span>2주차</span>
                  <span>3주차</span>
                  <span>완전 회복</span>
                </div>
              </div>
            )}
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginTop: '10px'
            }}>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                color: isDarkMode ? '#F9FAFB' : '#111827'
              }}>
                내 수술 캘린더
              </h3>
            </div>
          </div>
          
          {/* 월 네비게이션 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <button
              onClick={() => changeMonth('prev')}
              style={{
                border: 'none',
                backgroundColor: 'transparent',
                color: isDarkMode ? '#E5E7EB' : '#4B5563',
                fontSize: '18px',
                cursor: 'pointer'
              }}
            >
              ←
            </button>
            <div style={{
              fontWeight: 'bold',
              color: isDarkMode ? '#E5E7EB' : '#4B5563'
            }}>
              {currentMonth.format('YYYY년 MM월')}
            </div>
            <button
              onClick={() => changeMonth('next')}
              style={{
                border: 'none',
                backgroundColor: 'transparent',
                color: isDarkMode ? '#E5E7EB' : '#4B5563',
                fontSize: '18px',
                cursor: 'pointer'
              }}
            >
              →
            </button>
          </div>
          
          {/* 요일 헤더 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: isDarkMode ? '#9CA3AF' : '#6B7280',
          }}>
            {['일', '월', '화', '수', '목', '금', '토'].map((day, i) => (
              <div key={day} style={{
                color: i === 0 ? colors.highlight : i === 6 ? colors.secondary : 'inherit'
              }}>
                {day}
              </div>
            ))}
          </div>
          
          {/* 날짜 그리드 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginBottom: '16px'
          }}>
            {Array.from({ length: 42 }).map((_, index) => {
              const date = dayjs(currentMonth).startOf('month').startOf('week').add(index, 'day');
              const dateStr = date.format('YYYY-MM-DD');
              const isCurrentMonth = date.month() === currentMonth.month();
              const isToday = dateStr === dayjs().format('YYYY-MM-DD');
              const isSelected = dateStr === selectedDay;
              const dateColor = getDateColor(dateStr);
              
              return (
                <div 
                  key={index}
                  onClick={() => handleDayClick(dateStr)}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    height: '40px',
                    width: '100%',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    backgroundColor: isSelected 
                      ? (isDarkMode ? '#374151' : '#EFF6FF') 
                      : 'transparent',
                    border: isToday 
                      ? `2px solid ${colors.primary}` 
                      : (isSelected ? `1px solid ${colors.primary}` : 'none'),
                    opacity: isCurrentMonth ? 1 : 0.3,
                    position: 'relative',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    fontSize: '14px',
                    fontWeight: isToday || isSelected ? 'bold' : 'normal',
                    color: dateColor 
                      ? (isDarkMode ? 'white' : 'black')
                      : (isDarkMode ? '#E5E7EB' : '#111827')
                  }}>
                    {date.date()}
                  </div>
                  
                  {/* 표시 마커 - 해당 날짜의 스케줄 확인 */}
                  {(() => {
                    const schedulesForThisDate = getSchedulesForDate(dateStr);
                    
                    if (schedulesForThisDate.length === 0) return null;
                    
                    // 여러 스케줄이 있으면 색상별 마커 표시
                    if (schedulesForThisDate.length > 1) {
                      // 마커 컨테이너 스타일
                      return (
                        <div style={{
                          display: 'flex',
                          gap: '2px',
                          justifyContent: 'center',
                          marginTop: '2px'
                        }}>
                          {schedulesForThisDate.map((s, i) => {
                            // 수술 타입별 색상 지정
                            const getScheduleColor = (type: SurgeryType, eventType: 'consultation' | 'surgery' | 'stitchRemoval') => {
                              // 기본 이벤트 색상
                              const baseColor = eventType === 'consultation' ? colors.secondary : 
                                           eventType === 'surgery' ? colors.highlight : colors.primary;
                              
                              // 수술 타입별로 색상 변형 (수술일의 경우)
                              if (eventType === 'surgery') {
                                return getSurgeryTypeColor(type);
                              }
                              
                              return baseColor;
                            };
                            
                            // 이벤트 타입 결정
                            let eventType: 'consultation' | 'surgery' | 'stitchRemoval' = 'surgery';
                            if (s.consultationDate === dateStr) {
                              eventType = 'consultation';
                            } else if (s.stitchRemovalDate === dateStr) {
                              eventType = 'stitchRemoval';
                            }
                            
                            // 수술 타입별 마커 스타일 지정
                            return (
                              <div key={i} style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: getScheduleColor(s.type, eventType),
                                border: s.id === selectedScheduleId ? '1px solid white' : 'none',
                                boxShadow: s.id === selectedScheduleId ? '0 0 0 1px rgba(0,0,0,0.2)' : 'none'
                              }} />
                            );
                          })}
                        </div>
                      );
                    }
                    
                    // 수술일은 특별한 표시 (하단 바)
                    const hasSurgery = schedulesForThisDate.some(s => s.surgeryDate === dateStr);
                    
                    if (hasSurgery) {
                      // 수술 타입별 색상 지정
                      const surgerySchedule = schedulesForThisDate.find(s => s.surgeryDate === dateStr);
                      const surgeryColor = surgerySchedule ? 
                        getSurgeryTypeColor(surgerySchedule.type) 
                        : colors.highlight;
                      
                      return (
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          backgroundColor: surgeryColor,
                          borderRadius: '0 0 8px 8px'
                        }} />
                      );
                    }
                    
                    // 단일 스케줄은 원형 표시
                    return (
                      <div style={{
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: dateColor || 'transparent',
                        marginTop: '2px',
                        marginLeft: 'auto',
                        marginRight: 'auto'
                      }} />
                    );
                  })()}
                </div>
              );
            })}
          </div>
          
          {/* 일정 범례 */}
          <div style={{
            marginBottom: '16px',
            fontSize: '12px',
            color: isDarkMode ? '#D1D5DB' : '#4B5563',
          }}>
            {/* 기본 일정 범례 */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              marginBottom: '12px',
              justifyContent: 'center',
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: isDarkMode ? '#1F2937' : '#F3F4F6',
                padding: '6px 10px',
                borderRadius: '20px'
              }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10B981', marginRight: '6px' }} />
                <span style={{ color: isDarkMode ? 'white' : '#000000' }}>상담일</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: isDarkMode ? '#1F2937' : '#F3F4F6',
                padding: '6px 10px',
                borderRadius: '20px',
                borderBottom: `2px solid #EF4444`
              }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#EF4444', marginRight: '6px' }} />
                <span style={{ color: isDarkMode ? 'white' : '#000000' }}>수술일</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: isDarkMode ? '#1F2937' : '#F3F4F6',
                padding: '6px 10px',
                borderRadius: '20px'
              }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3B82F6', marginRight: '6px' }} />
                <span style={{ color: isDarkMode ? 'white' : '#000000' }}>실밥제거일</span>
              </div>
            </div>
            
            {/* 수술 타입별 색상 범례 (여러 수술 스케줄이 있는 경우만 표시) */}
            {surgerySchedules.length > 1 && (
              <div style={{
                padding: '10px',
                backgroundColor: isDarkMode ? '#1F2937' : '#F3F4F6',
                borderRadius: '8px',
              }}>
                <div style={{
                  marginBottom: '8px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: isDarkMode ? 'white' : '#000000'
                }}>
                  수술 유형별 색상
                </div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  justifyContent: 'center',
                }}>
                  {surgerySchedules
                    .map(s => s.type)
                    .filter((type, index, self) => self.indexOf(type) === index) // 중복 제거
                    .map((type) => {
                      // 수술 타입별 색상 지정
                      const typeColor = getSurgeryTypeColor(type);
                        
                      return (
                        <div key={type} style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          backgroundColor: isDarkMode ? '#374151' : 'white',
                          padding: '4px 8px',
                          borderRadius: '16px',
                          border: `1px solid ${typeColor}`
                        }}>
                          <div style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%', 
                            backgroundColor: typeColor, 
                            marginRight: '4px'
                          }} />
                          <span style={{ color: isDarkMode ? 'white' : '#000000' }}>{type}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
          
          {/* 선택된 일정 정보 */}
          {surgerySchedules.length > 0 && (
            <div style={{
              margin: '12px 0',
              padding: '8px 12px',
              backgroundColor: isDarkMode ? '#1F2937' : '#EFF6FF',
              borderRadius: '8px',
              fontSize: '13px',
              color: isDarkMode ? '#E5E7EB' : '#4B5563',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ fontWeight: 'bold', color: isDarkMode ? '#FFFFFF' : '#000000' }}>선택된 수술 일정:</span>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  {/* 새 수술 추가 버튼 */}
                  <button
                    onClick={() => {
                      createNewSchedule();
                      setCurrentView('settings');
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '50px',
                      backgroundColor: '#10B981', // 녹색
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 'bold',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0 3px 8px rgba(16, 185, 129, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 4px 10px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 3px 8px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>+</span>
                    새 수술
                  </button>
                
                  {/* 기존 여정 꾸미기 버튼 */}
                  <button 
                    onClick={() => setCurrentView('settings')}
                    style={{
                      marginLeft: '8px',
                      padding: '7px 14px',
                      borderRadius: '50px',
                      backgroundColor: '#E83E8C', // 핑크
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 'bold',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(232, 62, 140, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 6px 15px rgba(232, 62, 140, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(232, 62, 140, 0.3)';
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.7 10.4C19.7 10.4 19.7 10.4 19.7 10.4L19.6 10.4L18.4 9.7L18.4 9.8C18.3 9.6 18.3 9.5 18.3 9.4C18.3 9.3 18.3 9.3 18.3 9.2C18.3 8.7 18.4 8.4 18.5 8.1L16.2 6.7C15.8 7.1 15.4 7.3 14.8 7.3C14.4 7.3 14 7.1 13.6 6.9C13.3 6.6 13.1 6.3 13.1 5.8C13.1 5.5 13.1 5.2 13.2 4.9L10.8 4.1C10.6 4.6 10.2 4.9 9.7 4.9C9.4 4.9 9.1 4.8 8.9 4.6C8.6 4.4 8.5 4.1 8.5 3.7C8.5 3.6 8.5 3.6 8.5 3.5C8.5 3.5 8.5 3.5 8.5 3.4L6.6 3L6.3 3C5.7 3 5.2 3.5 5.2 4.1L4.3 19.6C4.3 20.2 4.8 20.7 5.4 20.7L5.7 20.7L19.1 20.7C19.7 20.7 20.2 20.2 20.2 19.6L19.7 10.4Z" fill="white"/>
                      <path d="M9.2 3.3C9.2 3.3 9.2 3.4 9.2 3.5C9.2 3.6 9.2 3.7 9.3 3.8C9.3 3.9 9.4 3.9 9.5 4C9.6 4 9.7 4.1 9.8 4.1C9.9 4.1 10 4 10.1 4C10.2 3.9 10.3 3.9 10.3 3.8C10.4 3.7 10.4 3.6 10.4 3.5C10.4 3.4 10.4 3.3 10.4 3.2C10.4 3.1 10.3 3 10.3 2.9C10.2 2.8 10.2 2.8 10.1 2.7C10 2.7 9.9 2.6 9.8 2.6C9.7 2.6 9.6 2.7 9.5 2.7C9.4 2.8 9.3 2.8 9.3 2.9C9.2 3 9.2 3.1 9.2 3.3Z" fill="white"/>
                      <path d="M13.7 5.9C13.7 6 13.7 6.1 13.8 6.2C13.8 6.3 13.9 6.3 14 6.4C14.1 6.4 14.2 6.5 14.3 6.5C14.4 6.5 14.5 6.4 14.6 6.4C14.7 6.3 14.8 6.3 14.8 6.2C14.9 6.1 14.9 6 14.9 5.9C14.9 5.8 14.9 5.7 14.9 5.6C14.9 5.5 14.8 5.4 14.8 5.3C14.7 5.2 14.7 5.2 14.6 5.1C14.5 5.1 14.4 5 14.3 5C14.2 5 14.1 5.1 14 5.1C13.9 5.2 13.8 5.2 13.8 5.3C13.7 5.4 13.7 5.5 13.7 5.6C13.7 5.7 13.7 5.8 13.7 5.9Z" fill="white"/>
                      <path d="M18.9 9.3C18.9 9.4 19 9.5 19 9.6C19 9.7 19.1 9.7 19.2 9.8C19.2 9.8 19.3 9.9 19.4 9.9C19.5 9.9 19.6 9.8 19.7 9.8C19.8 9.7 19.9 9.7 19.9 9.6C20 9.5 20 9.4 20 9.3C20 9.2 20 9.1 20 9C20 8.9 19.9 8.8 19.9 8.7C19.8 8.6 19.8 8.6 19.7 8.5C19.6 8.5 19.5 8.4 19.4 8.4C19.3 8.4 19.2 8.5 19.2 8.5C19.1 8.6 19 8.6 19 8.7C18.9 8.8 18.9 8.9 18.9 9C18.9 9.1 18.9 9.2 18.9 9.3Z" fill="white"/>
                    </svg>
                    수술 정보 편집
                  </button>
                  {surgerySchedules.map(schedule => {
                    // 수술 타입별 색상 지정
                    const typeColor = getSurgeryTypeColor(schedule.type);
                      
                    const isSelected = schedule.id === selectedScheduleId;
                    
                    return (
                      <button
                        key={schedule.id}
                        onClick={() => setSelectedScheduleId(schedule.id)}
                        style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          backgroundColor: isSelected 
                            ? typeColor 
                            : (isDarkMode ? '#374151' : '#F3F4F6'),
                          color: isSelected 
                            ? 'white' 
                            : (isDarkMode ? '#E5E7EB' : '#1F2937'),
                          border: `1px solid ${isSelected ? 'transparent' : typeColor}`,
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div style={{ 
                          width: '6px', 
                          height: '6px', 
                          borderRadius: '50%', 
                          backgroundColor: isSelected ? 'white' : typeColor
                        }} />
                        {schedule.type}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* 선택된 일정 세부 정보 */}
              {selectedScheduleId && (
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  fontSize: '12px'
                }}>
                  {(() => {
                    const schedule = surgerySchedules.find(s => s.id === selectedScheduleId);
                    if (!schedule) return null;
                    
                    return (
                      <>
                        {schedule.consultationDate && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: '#10B981', fontWeight: 'bold' }}>●</span>
                            <span style={{ color: isDarkMode ? 'white' : '#000000' }}>상담: {schedule.consultationDate}</span>
                          </div>
                        )}
                        {schedule.surgeryDate && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: '#EF4444', fontWeight: 'bold' }}>●</span>
                            <span style={{ color: isDarkMode ? 'white' : '#000000' }}>수술: {schedule.surgeryDate}</span>
                          </div>
                        )}
                        {schedule.stitchRemovalDate && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: '#3B82F6', fontWeight: 'bold' }}>●</span>
                            <span style={{ color: isDarkMode ? 'white' : '#000000' }}>실밥제거: {schedule.stitchRemovalDate}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
          
          {/* 안내 메시지 및 수술 추가 버튼 */}
          <div style={{
            margin: '12px 0',
            padding: '8px 12px',
            backgroundColor: isDarkMode ? '#1F2937' : '#EFF6FF',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            {/* 안내 메시지 */}
            <div style={{
              textAlign: 'center',
              fontSize: '13px',
              color: isDarkMode ? '#E5E7EB' : '#4B5563',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>👆</span>
              <span style={{ color: isDarkMode ? 'white' : '#000000' }}>날짜를 클릭하여 맞춤형 케어 정보를 확인하세요</span>
            </div>
            
            {/* 새 수술 추가 버튼 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '5px'
            }}>
              <button
                onClick={() => {
                  // 새 스케줄 생성
                  createNewSchedule();
                  // 설정 화면으로 전환
                  setCurrentView('settings');
                }}
                style={{
                  padding: '10px 15px',
                  borderRadius: '8px',
                  backgroundColor: '#E83E8C',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 6px 8px rgba(0, 0, 0, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
              >
                <span style={{ fontSize: '18px' }}>+</span>
                새 수술 일정 추가
              </button>
            </div>
          </div>
          
          {/* 케어 정보 */}
          {careInfo && (
            <div style={{
              padding: '16px',
              borderRadius: '8px',
              backgroundColor: isDarkMode ? '#374151' : 'white',
              marginTop: '16px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              color: isDarkMode ? '#E5E7EB' : '#1F2937',
              maxHeight: '60vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ 
                marginBottom: '12px',
                fontSize: '18px',
                fontWeight: 'bold',
                color: isDarkMode ? '#FFFFFF' : '#000000',
                paddingBottom: '8px',
                borderBottom: `1px solid ${isDarkMode ? '#4B5563' : '#E5E7EB'}`
              }}>
                {(() => {
                  // 선택된 수술 스케줄 확인
                  const activeSchedule = surgerySchedules.find(s => s.id === selectedScheduleId);
                  
                  // 색상 가져오기
                  const getTypeColor = (type: SurgeryType | undefined) => {
                    if (!type) return '#6B7280';
                    return getSurgeryTypeColor(type);
                  };

                  // 케어 정보 헤더 텍스트
                  const headerText = (!selectedDay || !activeSchedule) 
                    ? '케어 정보' 
                    : `케어 정보 - ${activeSchedule.type}`;
                  
                  // 선택된 수술 유형에 맞는 스타일 적용
                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {activeSchedule && (
                        <div style={{ 
                          width: '12px', 
                          height: '12px', 
                          borderRadius: '50%', 
                          backgroundColor: getTypeColor(activeSchedule.type),
                          marginRight: '8px',
                          boxShadow: `0 0 0 2px ${isDarkMode ? '#374151' : 'white'}, 0 0 0 3px ${getTypeColor(activeSchedule.type)}`
                        }} />
                      )}
                      <span>{headerText}</span>
                    </div>
                  );
                })()}
              </h3>
              
              {/* 중복된 수술 정보가 있을 경우 수술 선택 탭 표시 */}
              {(() => {
                if (!selectedDay) return null;
                
                const schedulesForDate = getSchedulesForDate(selectedDay);
                if (schedulesForDate.length <= 1) return null;
                
                return (
                  <div style={{
                    display: 'flex',
                    gap: '6px',
                    marginBottom: '16px',
                    backgroundColor: isDarkMode ? '#1F2937' : '#F3F4F6',
                    padding: '8px',
                    borderRadius: '8px',
                    overflow: 'auto'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      marginRight: '6px',
                      justifyContent: 'center'
                    }}>
                      <span style={{
                        fontSize: '11px',
                        color: isDarkMode ? '#9CA3AF' : '#6B7280',
                        whiteSpace: 'nowrap'
                      }}>
                        수술 선택:
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      overflow: 'auto',
                      paddingBottom: '4px'
                    }}>
                      {schedulesForDate.map(schedule => {
                        // 수술 타입별 색상 지정
                        const typeColor = 
                          schedule.type === '코성형' ? '#ef4444' : 
                          schedule.type === '눈성형' ? '#3b82f6' : 
                          schedule.type === '안면윤곽' ? '#8b5cf6' : 
                          schedule.type === '가슴성형' ? '#ec4899' : 
                          schedule.type === '지방이식' ? '#f59e0b' : '#10b981';
                          
                        const isSelected = schedule.id === selectedScheduleId;
                        
                        // 이 날짜가 어떤 이벤트인지 확인 (상담일/수술일/실밥제거일)
                        let eventType = '';
                        let eventIcon = '';
                        
                        if (selectedDay === schedule.consultationDate) {
                          eventType = '상담';
                          eventIcon = '🗓️';
                        } else if (selectedDay === schedule.surgeryDate) {
                          eventType = '수술';
                          eventIcon = '🏥';
                        } else if (selectedDay === schedule.stitchRemovalDate) {
                          eventType = '실밥제거';
                          eventIcon = '🩹';
                        }
                        
                        return (
                          <button
                            key={schedule.id}
                            onClick={() => setSelectedScheduleId(schedule.id)}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: isSelected ? typeColor : (isDarkMode ? '#374151' : 'white'),
                              color: isSelected ? 'white' : (isDarkMode ? '#E5E7EB' : '#1F2937'),
                              border: `1px solid ${isSelected ? 'transparent' : typeColor}`,
                              borderRadius: '6px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              minWidth: 'fit-content',
                              whiteSpace: 'nowrap',
                              fontWeight: isSelected ? 'bold' : 'normal',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '3px',
                              boxShadow: isSelected ? `0 2px 4px rgba(0,0,0,0.1)` : 'none',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <span style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <div style={{ 
                                width: '7px', 
                                height: '7px', 
                                borderRadius: '50%', 
                                backgroundColor: isSelected ? 'white' : typeColor
                              }} />
                              {schedule.type}
                            </span>
                            {eventType && (
                              <span style={{
                                fontSize: '10px',
                                opacity: 0.8,
                                backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
                                padding: '2px 4px',
                                borderRadius: '4px',
                                marginTop: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px'
                              }}>
                                {eventIcon} {eventType}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              
              <div 
                style={{ fontSize: '14px', lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    // 특수 HTML 태그 처리 (span 태그 등)
                    if (careInfo.includes('<span') || careInfo.includes('=====')) {
                      // 중복 일정이 있는 경우의 특별 처리
                      let processedHtml = careInfo;
                      
                      // 구분선 처리
                      processedHtml = processedHtml.replace(/- - - - - - - - - - - - - - -/g, 
                        `<hr style="border: none; border-top: 1px dashed ${isDarkMode ? '#4B5563' : '#D1D5DB'}; margin: 20px 0;" />`);
                      
                      // 다른 텍스트 처리 (기존 처리 로직 유지)
                      const sections = processedHtml.split('\n\n');
                      const formattedSections = sections.map(section => {
                        // 불릿 포인트 목록 처리
                        if (section.trim().startsWith('•')) {
                          const bulletPoints = section.split('\n').map(line => 
                            `<div style="margin-bottom: 6px;">${line.trim()}</div>`
                          ).join('');
                          
                          return `<div style="margin-bottom: 16px;">${bulletPoints}</div>`;
                        } 
                        // 아이콘이 있는 섹션은 카드 형태로 처리
                        else if (section.includes('🥤') || section.includes('🍽️') || section.includes('🏋️') || 
                                 section.includes('👨‍💼') || section.includes('🚗') || section.includes('🧳') || 
                                 section.includes('✈️')) {
                          
                          const lines = section.split('\n').filter(line => line.trim());
                          const cardsHtml = lines.map(line => {
                            const parts = line.split(':');
                            if (parts.length < 2) return '';
                            
                            return `
                              <div style="
                                padding: 6px 10px;
                                border-radius: 6px;
                                background-color: ${isDarkMode ? '#1F2937' : '#F3F4F6'};
                                display: inline-block;
                                text-align: center;
                                min-width: 80px;
                                margin: 4px;
                                flex: 1 0 auto;
                                max-width: calc(50% - 8px);
                              ">
                                <div style="font-size: 16px; margin-bottom: 4px;">${parts[0].trim()}</div>
                                <div style="
                                  font-size: 12px;
                                  text-align: center;
                                  color: ${isDarkMode ? '#9CA3AF' : '#4B5563'};
                                ">
                                  ${parts[1].trim()}
                                </div>
                              </div>
                            `;
                          }).join('');
                          
                          return `<div style="
                            display: flex;
                            flex-wrap: wrap;
                            gap: 8px;
                            margin-bottom: 16px;
                          ">${cardsHtml}</div>`;
                        } 
                        // 준비물, 팁, 금지사항 섹션 처리
                        else if (section.includes('준비물:') || section.includes('빠른 회복 팁:') || section.includes('절대 금지사항:')) {
                          const titleParts = section.split(':');
                          const title = titleParts[0] + ':';
                          const content = titleParts.slice(1).join(':');
                          
                          const bgColor = section.includes('준비물:') 
                            ? (isDarkMode ? '#065F46' : '#ECFDF5') 
                            : section.includes('빠른 회복 팁:') 
                              ? (isDarkMode ? '#1E40AF' : '#EFF6FF') 
                              : (isDarkMode ? '#991B1B' : '#FEF2F2');
                          
                          return `
                            <div style="
                              margin-bottom: 16px;
                              padding: 10px;
                              border-radius: 6px;
                              background-color: ${isDarkMode ? '#1F2937' : '#F3F4F6'};
                            ">
                              <div style="
                                font-weight: bold;
                                margin-bottom: 6px;
                                background-color: ${bgColor};
                                color: ${isDarkMode ? 'white' : '#000000'};
                                padding: 4px 8px;
                                border-radius: 4px;
                              ">
                                ${title}
                              </div>
                              <div>${content.trim()}</div>
                            </div>
                          `;
                        } 
                        // 일반 섹션 처리
                        else {
                          const lines = section.split('\n');
                          const title = lines[0];
                          const content = lines.slice(1).join('<br>');
                          
                          if (!title) return section;
                          
                          return `
                            <div style="margin-bottom: 20px;">
                              <h4 style="
                                font-size: 16px;
                                font-weight: bold;
                                margin-bottom: 8px;
                                color: ${isDarkMode ? '#FFFFFF' : '#000000'};
                              ">
                                ${title}
                              </h4>
                              <div>${content}</div>
                            </div>
                          `;
                        }
                      });
                      
                      return formattedSections.join('');
                    } else {
                      // 기존 텍스트 포맷 처리 로직 (일반 메시지)
                      return careInfo.split('\n\n').map(section => {
                        // 불릿 포인트 목록 처리
                        if (section.trim().startsWith('•')) {
                          const bulletPoints = section.split('\n').map(line => 
                            `<div style="margin-bottom: 6px;">${line.trim()}</div>`
                          ).join('');
                          
                          return `<div style="margin-bottom: 16px;">${bulletPoints}</div>`;
                        } 
                        // 아이콘이 있는 섹션은 카드 형태로 처리
                        else if (section.includes('🥤') || section.includes('🍽️') || section.includes('🏋️') || 
                                 section.includes('👨‍💼') || section.includes('🚗') || section.includes('🧳') || 
                                 section.includes('✈️')) {
                          
                          const lines = section.split('\n').filter(line => line.trim());
                          const cardsHtml = lines.map(line => {
                            const parts = line.split(':');
                            if (parts.length < 2) return '';
                            
                            return `
                              <div style="
                                padding: 6px 10px;
                                border-radius: 6px;
                                background-color: ${isDarkMode ? '#1F2937' : '#F3F4F6'};
                                display: inline-block;
                                text-align: center;
                                min-width: 80px;
                                margin: 4px;
                                flex: 1 0 auto;
                                max-width: calc(50% - 8px);
                              ">
                                <div style="font-size: 16px; margin-bottom: 4px;">${parts[0].trim()}</div>
                                <div style="
                                  font-size: 12px;
                                  text-align: center;
                                  color: ${isDarkMode ? '#9CA3AF' : '#4B5563'};
                                ">
                                  ${parts[1].trim()}
                                </div>
                              </div>
                            `;
                          }).join('');
                          
                          return `<div style="
                            display: flex;
                            flex-wrap: wrap;
                            gap: 8px;
                            margin-bottom: 16px;
                          ">${cardsHtml}</div>`;
                        } 
                        // 준비물, 팁, 금지사항 섹션 처리
                        else if (section.includes('준비물:') || section.includes('빠른 회복 팁:') || section.includes('절대 금지사항:')) {
                          const titleParts = section.split(':');
                          const title = titleParts[0] + ':';
                          const content = titleParts.slice(1).join(':');
                          
                          const bgColor = section.includes('준비물:') 
                            ? (isDarkMode ? '#065F46' : '#ECFDF5') 
                            : section.includes('빠른 회복 팁:') 
                              ? (isDarkMode ? '#1E40AF' : '#EFF6FF') 
                              : (isDarkMode ? '#991B1B' : '#FEF2F2');
                          
                          return `
                            <div style="
                              margin-bottom: 16px;
                              padding: 10px;
                              border-radius: 6px;
                              background-color: ${isDarkMode ? '#1F2937' : '#F3F4F6'};
                            ">
                              <div style="
                                font-weight: bold;
                                margin-bottom: 6px;
                                background-color: ${bgColor};
                                color: ${isDarkMode ? 'white' : '#000000'};
                                padding: 4px 8px;
                                border-radius: 4px;
                              ">
                                ${title}
                              </div>
                              <div>${content.trim()}</div>
                            </div>
                          `;
                        } 
                        // 일반 섹션 처리
                        else {
                          const lines = section.split('\n');
                          const title = lines[0];
                          const content = lines.slice(1).join('<br>');
                          
                          if (!title) return section;
                          
                          return `
                            <div style="margin-bottom: 20px;">
                              <h4 style="
                                font-size: 16px;
                                font-weight: bold;
                                margin-bottom: 8px;
                                color: ${isDarkMode ? '#FFFFFF' : '#000000'};
                              ">
                                ${title}
                              </h4>
                              <div>${content}</div>
                            </div>
                          `;
                        }
                      }).join('');
                    }
                  })()
                }}
              />
            </div>
          )}
        </div>
      ) : (
        /* 설정 화면 */
        <div style={{ 
          backgroundColor: isDarkMode ? '#1F2937' : '#F9FAFB',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          padding: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: 'bold',
              color: isDarkMode ? '#F9FAFB' : '#111827'
            }}>
              수술 정보 설정
            </h2>
            <button 
              onClick={() => setCurrentView('calendar')}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                backgroundColor: colors.primary,
                color: 'white',
                fontSize: '14px',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              캘린더로 돌아가기
            </button>
          </div>
          
          {/* 설정 폼 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* 기존 일정 목록 */}
            {surgerySchedules.length > 0 && (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <label style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: isDarkMode ? '#E5E7EB' : '#111827'
                  }}>
                    기존 수술 일정
                  </label>
                  <button 
                    onClick={() => {
                      // 새 일정 생성
                      const newSchedule = {
                        id: Date.now().toString(),
                        type: '코성형',
                        consultationDate: null,
                        surgeryDate: null,
                        stitchRemovalDate: null
                      };
                      setCurrentSchedule(newSchedule);
                    }}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      backgroundColor: colors.primary,
                      color: 'white',
                      fontSize: '13px',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    + 새 일정 추가
                  </button>
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  padding: '8px',
                  backgroundColor: isDarkMode ? '#1F2937' : '#F3F4F6',
                  borderRadius: '8px'
                }}>
                  {surgerySchedules.map(schedule => (
                    <div 
                      key={schedule.id}
                      style={{
                        padding: '10px',
                        borderRadius: '6px',
                        backgroundColor: schedule.id === currentSchedule.id 
                          ? (isDarkMode ? '#374151' : '#E5E7EB') 
                          : (isDarkMode ? '#111827' : 'white'),
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => setCurrentSchedule(schedule)}
                    >
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          {schedule.type}
                        </div>
                        <div style={{ 
                          fontSize: '12px',
                          color: isDarkMode ? '#9CA3AF' : '#6B7280',
                          display: 'flex',
                          gap: '8px'
                        }}>
                          {schedule.surgeryDate && (
                            <span>수술일: {schedule.surgeryDate}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSchedule(schedule.id);
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: 'transparent',
                          color: isDarkMode ? '#9CA3AF' : '#6B7280',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        삭제
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* 현재 편집 중인 일정 */}
            <div style={{
              padding: '16px',
              backgroundColor: isDarkMode ? '#1F2937' : '#F9FAFB',
              borderRadius: '8px',
              border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`
            }}>
              <div style={{
                marginBottom: '16px',
                fontWeight: 'bold',
                color: colors.primary,
                fontSize: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>수술 정보 {surgerySchedules.length === 0 ? '입력' : '편집'}</span>
                {surgerySchedules.length === 0 && (
                  <div style={{
                    fontSize: '13px',
                    color: isDarkMode ? '#9CA3AF' : '#6B7280',
                    fontWeight: 'normal'
                  }}>
                    첫 번째 일정을 등록하세요
                  </div>
                )}
              </div>
              
              {/* 수술 종류 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: isDarkMode ? '#E5E7EB' : '#4B5563'
                }}>
                  수술 종류
                </label>
                
                {/* 수술 유형 선택 또는 직접 입력 */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '8px'
                }}>
                  {/* 일반 수술 유형 선택 */}
                  <select
                    value={
                      ['코성형', '눈성형', '안면윤곽', '가슴성형', '지방이식', '기타'].includes(currentSchedule.type) 
                        ? currentSchedule.type 
                        : '기타'
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      setCurrentSchedule({
                        ...currentSchedule, 
                        type: value === '기타' && currentSchedule.type !== '기타' 
                          ? currentSchedule.type  // 사용자 정의 값 유지
                          : value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: `1px solid ${isDarkMode ? '#4B5563' : '#D1D5DB'}`,
                      backgroundColor: isDarkMode ? '#374151' : 'white',
                      color: isDarkMode ? '#E5E7EB' : '#111827',
                      fontSize: '14px'
                    }}
                  >
                    <option value="코성형">코성형</option>
                    <option value="눈성형">눈성형</option>
                    <option value="안면윤곽">안면윤곽</option>
                    <option value="가슴성형">가슴성형</option>
                    <option value="지방이식">지방이식</option>
                    <option value="기타">직접 입력</option>
                  </select>
                  
                  {/* 사용자 정의 수술 유형 입력 */}
                  {(['기타'].includes(currentSchedule.type) || 
                    !['코성형', '눈성형', '안면윤곽', '가슴성형', '지방이식', '기타'].includes(currentSchedule.type)) && (
                    <input
                      type="text"
                      value={currentSchedule.type === '기타' ? '' : currentSchedule.type}
                      onChange={(e) => setCurrentSchedule({...currentSchedule, type: e.target.value || '기타'})}
                      placeholder="수술 유형을 입력하세요"
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: `1px solid ${isDarkMode ? '#4B5563' : '#D1D5DB'}`,
                        backgroundColor: isDarkMode ? '#374151' : 'white',
                        color: isDarkMode ? '#E5E7EB' : '#111827',
                        fontSize: '14px'
                      }}
                    />
                  )}
                </div>
              </div>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '12px',
                marginBottom: '16px'
              }}>
                {/* 상담 날짜 */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    color: isDarkMode ? '#E5E7EB' : '#4B5563'
                  }}>
                    상담 날짜
                  </label>
                  <input
                    type="date"
                    value={currentSchedule.consultationDate || ''}
                    onChange={(e) => setCurrentSchedule({...currentSchedule, consultationDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '8px',
                      border: `1px solid ${isDarkMode ? '#4B5563' : '#D1D5DB'}`,
                      backgroundColor: isDarkMode ? '#374151' : 'white',
                      color: isDarkMode ? '#E5E7EB' : '#111827',
                      fontSize: '13px'
                    }}
                  />
                </div>
                
                {/* 수술 날짜 */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    color: isDarkMode ? '#E5E7EB' : '#4B5563'
                  }}>
                    수술 날짜
                  </label>
                  <input
                    type="date"
                    value={currentSchedule.surgeryDate || ''}
                    onChange={(e) => setCurrentSchedule({...currentSchedule, surgeryDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '8px',
                      border: `1px solid ${isDarkMode ? '#4B5563' : '#D1D5DB'}`,
                      backgroundColor: isDarkMode ? '#374151' : 'white',
                      color: isDarkMode ? '#E5E7EB' : '#111827',
                      fontSize: '13px'
                    }}
                  />
                </div>
                
                {/* 실밥 제거 날짜 */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    color: isDarkMode ? '#E5E7EB' : '#4B5563'
                  }}>
                    실밥 제거 날짜
                  </label>
                  <input
                    type="date"
                    value={currentSchedule.stitchRemovalDate || ''}
                    onChange={(e) => setCurrentSchedule({...currentSchedule, stitchRemovalDate: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '8px',
                      border: `1px solid ${isDarkMode ? '#4B5563' : '#D1D5DB'}`,
                      backgroundColor: isDarkMode ? '#374151' : 'white',
                      color: isDarkMode ? '#E5E7EB' : '#111827',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* 버튼 영역 */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '8px'
            }}>
              <button
                onClick={() => setCurrentView('calendar')}
                style={{
                  flex: '1',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? '#374151' : '#E5E7EB',
                  color: isDarkMode ? '#E5E7EB' : '#1F2937',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
              
              {/* 새 수술 일정 추가 버튼 */}
              {surgerySchedules.length > 0 && (
                <button
                  onClick={() => {
                    createNewSchedule();
                  }}
                  style={{
                    flex: '1',
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: colors.primary,
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>+</span>
                  새 수술 추가
                </button>
              )}
              
              <button
                onClick={() => {
                  if (!currentSchedule.surgeryDate) {
                    alert('수술 날짜를 입력해주세요');
                    return;
                  }
                  saveCurrentSchedule();
                }}
                style={{
                  flex: '2',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: colors.secondary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                저장하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}